import { usePageBackOptions } from '@/contexts/PageNavigationContext';
import { documentListPath } from '@/lib/pageNavigation';
import { customerPriceGroup, customerDiscount, customerFixedPrice } from '@/lib/customerPricing';
import { artikelVoorVariant } from '@/lib/artikelPrijs';
import { DocumentVatSelect } from '@/components/DocumentVatSelect';
import { vatLabel } from '@/lib/taxDisplay';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import API, { fmt, today, indDays, pdfFilename } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import BetalingsherinneringModal from '@/components/BetalingsherinneringModal';
import PackingListModal from '@/components/PackingListModal';
import LeverAdresBlok from '@/components/LeverAdresBlok';
import { lijnPrijsVan, stukPrijsVan } from '@/lib/artikelPrijs';
import { scanCostMultiplier, scanAmountCorrection, knownPurchasePrice } from '@/lib/scanQuantity';
import { syncScanDocumentDiscount, scanDocumentDiscountValid } from '@/lib/scanDiscount';
import { fetchPdfActions, openPdf, sharePdf, downloadPdf } from '@/lib/pdfActions';
import { isIOS } from '@/lib/platform';
import { vraagFotoKeuze, vraagPrijsKeuze } from '@/utils/pdfPromptFotos';
import { Plus, Trash, MagnifyingGlass, CurrencyEur, Bell, Paperclip, ClockCounterClockwise, Printer, EnvelopeSimple, DownloadSimple, Repeat, Calendar, Barcode, Package, Eye, Camera } from '@phosphor-icons/react';
import LeverFotosPanel from '@/components/LeverFotosPanel';
import KlantZoeker from '@/components/KlantZoeker';
import BijlagenPanel from '@/components/BijlagenPanel';
import DocMailsPanel from '@/components/DocMailsPanel';
import BarcodeScanner from '@/components/BarcodeScanner';
import VariantPickerModal from '@/components/VariantPickerModal';
import VariantMatrixModal from '@/components/VariantMatrixModal';
import { toast } from 'sonner';
import DocumentenStroom from '@/components/DocumentenStroom';
import RemotePrintButton from '@/components/RemotePrintButton';
import SignaturePad from '@/components/SignaturePad';
import ControlePanel from '@/components/ControlePanel';
import ArtikelFotosPanel, { LijnFoto, fotoSrc } from '@/components/ArtikelFotosPanel';
import VersturenModal from '@/components/VersturenModal';
import MailPreviewModal from '@/components/MailPreviewModal';
import VerzendlabelModal from '@/components/VerzendlabelModal';
import { fmtDec, pf } from '@/lib/numFmt';
import { detecteerBtwRegel, landNaarIso, klantStandaardBtw, artikelBtw } from '@/utils/btwKlant';

const TYPES = {
  offerte: { label: 'Offerte', statuses: ['concept', 'verzonden', 'goedgekeurd', 'afgewezen', 'geannuleerd', 'geblokkeerd'] },
  bestelbon: { label: 'Bestelbon', statuses: ['concept', 'verzonden', 'productie in afwachting', 'besteld', 'geannuleerd', 'geblokkeerd'] },
  leverbon: { label: 'Leveringsbon', statuses: ['concept', 'verzonden', 'productie in afwachting', 'nog te leveren', 'onderweg', 'geleverd', 'geannuleerd', 'geblokkeerd'] },
  proforma: { label: 'Proforma', statuses: ['concept', 'verzonden', 'geannuleerd', 'geblokkeerd'] },
  factuur: { label: 'Factuur', statuses: ['concept', 'openstaand', 'gedeeltelijk betaald', 'betaald', 'vervallen', 'geannuleerd', 'geblokkeerd'] },
  creditnota: { label: 'Creditnota', statuses: ['concept', 'verzonden', 'verwerkt', 'geannuleerd', 'geblokkeerd'] },
};
const TERMIJNEN = [
  { value: 0, label: 'Bij ontvangst' }, { value: 14, label: '14 dagen' },
  { value: 30, label: '30 dagen' }, { value: 60, label: '60 dagen' }, { value: 90, label: '90 dagen' },
];
const BTW_REGELS = [
  { key: 'medecontractant', label: 'Medecontractant (0% BTW)', btw: 0 },
  { key: 'intracommunautair', label: 'Intracommunautaire levering (0% BTW)', btw: 0 },
  { key: 'export_eu', label: 'Uitvoer buiten EU (0% BTW)', btw: 0 },
  { key: 'renovatie_10', label: 'Renovatie privéwoningen 10+ jaar (6% BTW)', btw: 6 },
  { key: 'renovatie_15', label: 'Renovatie privéwoningen 15+ jaar (6% BTW)', btw: 6 },
  { key: 'zonnepanelen', label: 'Zonnepanelen/warmtepompen woningen 10+ jaar (6% BTW)', btw: 6 },
];

const emptyLine = () => ({ aantal: 1, ref_search: '', referentie: '', omschr: '', gewicht: '', eenheid: 'st', korting: '', btw: 21, prijs: '', art_id: null, pack_info: '', ean: '', toon_ean: false, eenheid_aantal: 0, eenheid_label: '', toon_eenheid_prijs: true, combo_key: '', variant_combo: null, variant_label: '', stuks_effectief: 0 });

// Hulp: bouw " (€ 0,66 / L/m) (960/L/m)" suffix obv lijnprijs + eenheid op de lijn.
const eenheidSuffix = (l) => {
  try {
    if (l?.toon_eenheid_prijs === false) return '';
    const ea = parseFloat(l?.eenheid_aantal || 0);
    const el = (l?.eenheid_label || '').trim();
    const prijs = parseFloat(l?.prijs || 0);
    if (!(ea > 0) || !el || !(prijs > 0)) return '';
    const up = prijs / ea;
    // 2 decimalen standaard (conform alle documenten). Enkel <€0,01 → 4 decimalen om "€ 0,00" te vermijden.
    const upTxt = up >= 0.01 ? up.toFixed(2).replace('.', ',') : up.toFixed(4).replace('.', ',');
    const eaFmt = ea === Math.floor(ea) ? String(Math.floor(ea)) : ea.toFixed(4).replace(/\.?0+$/, '').replace('.', ',');
    return ` (€ ${upTxt} / ${el}) (${eaFmt}/${el})`;
  } catch { return ''; }
};

// Bereken effectief aantal stuks per pack: override via stuks_effectief, anders eenheid_aantal
const getStuksPerPack = (l) => {
  const eff = parseFloat(l?.stuks_effectief || 0);
  const ea = parseFloat(l?.eenheid_aantal || 0);
  if (eff > 0 && ea > 0 && eff !== ea) return eff;
  return ea;
};

function calcVerval(datum, termijn, eindeMaand) {
  const d = new Date(datum);
  d.setDate(d.getDate() + termijn);
  if (eindeMaand) { d.setMonth(d.getMonth() + 1); d.setDate(0); }
  return d.toISOString().split('T')[0];
}

// ── Ref zoeker (in line item) ───────────────────────────────────
// Mini-foto in de zoeklijsten (vaste 32×32 box; placeholder zonder foto zodat alles uitgelijnd blijft)
function ZoekThumb({ art }) {
  const src = fotoSrc(art?.foto_url);
  return (
    <span className="w-8 h-8 shrink-0 rounded-sm border border-gray-200 bg-white overflow-hidden inline-flex items-center justify-center" data-testid={src ? 'zoek-thumb' : 'zoek-thumb-leeg'}>
      {src ? <img src={src} alt="" loading="lazy" className="w-full h-full object-contain" /> : <span className="w-3 h-3 rounded-sm bg-gray-100" />}
    </span>
  );
}

function RefZoeker({ artikels, prijsGroep, btwOverride, onSelect, onFreeText, currentRef, metFoto }) {
  const [q, setQ] = useState(currentRef || '');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 340 });
  const refEl = useRef(null);
  const inputEl = useRef(null);

  // Sync lokale state wanneer currentRef van buiten verandert (bv. via Omschrijving-zoeker)
  useEffect(() => {
    setQ(currentRef || '');
  }, [currentRef]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (refEl.current && !refEl.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // Bereken dropdown-positie onder input (fixed om clipping te vermijden)
  const computePos = () => {
    if (!inputEl.current) return;
    const r = inputEl.current.getBoundingClientRect();
    setPos({ top: r.bottom + 2, left: r.left, width: Math.max(340, r.width) });
  };

  useEffect(() => {
    if (!open) return;
    computePos();
    const onScroll = () => computePos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onScroll); };
  }, [open]);

  // Lokale zoek: hoofdartikel art_code/omschrijving + alle varianten (varianten + variant_matrix.rijen)
  // Geeft een platte lijst met variant-info zodat user direct de juiste variant kan kiezen.
  const filtered = (() => {
    if (!q) return [];
    const ql = q.toLowerCase();
    const out = [];
    for (const a of artikels) {
      // Hoofdartikel match
      const hMatch = (a.art_code || '').toLowerCase().includes(ql)
        || (a.omschrijving || '').toLowerCase().includes(ql)
        || (a.ean || '').toLowerCase().includes(ql);
      if (hMatch) out.push({ art: a, variant: null });
      // Varianten match (flat + matrix). Veld kan 'art_code' OF 'code' heten.
      const allVars = [
        ...((a.varianten || []).map(v => ({ ...v, _src: 'flat' }))),
        ...(((a.variant_matrix || {}).rijen || []).map(v => ({ ...v, _src: 'matrix' }))),
      ];
      for (const v of allVars) {
        const vCode = v.art_code || v.code || '';
        const vEan = v.ean || '';
        if (vCode.toLowerCase().includes(ql) || vEan.toLowerCase().includes(ql)) {
          out.push({ art: a, variant: v });
        }
      }
      if (out.length >= 12) break;
    }
    return out.slice(0, 12);
  })();

  return (
    <div className="relative" style={{ overflow: 'visible', minWidth: 136 }} ref={refEl}>
      <input ref={inputEl} className="field-input" style={{ padding: '4px 6px', fontSize: 12, width: '100%' }}
        value={q} placeholder="Ref..."
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => q && setOpen(true)}
        onBlur={() => { setTimeout(() => setOpen(false), 200); if (onFreeText) onFreeText(q); }}
        data-testid="line-ref-search" />
      {open && filtered.length > 0 && (
        <div className="bg-white border border-gray-200 shadow-xl max-h-60 overflow-y-auto" style={{ position: 'fixed', zIndex: 9999, top: pos.top, left: pos.left, minWidth: pos.width }} data-testid="ref-dropdown">
          {filtered.map(({ art: a, variant: v }, idx) => {
            const baseP = a.prijzen?.[prijsGroep] || 0;
            const variantP = v?.prijs_override != null ? v.prijs_override : null;
            const eenheidsP = variantP != null ? variantP : baseP;
            const packPrijs = lijnPrijsVan(a, eenheidsP);
            const vCode = v?.art_code || v?.code;
            const displayCode = vCode || a.art_code;
            const comboStr = v?.combo ? Object.entries(v.combo).map(([k, val]) => `${k}: ${val}`).join(' · ') : '';
            const voorraad = v?.voorraad != null ? v.voorraad : a.voorraad;
            return (
              <div key={`${a.id}-${vCode || 'main'}-${idx}`} className={`flex items-center justify-between px-3 ${metFoto ? 'py-1.5' : 'py-2'} hover:bg-[#F0F4FF] cursor-pointer border-b border-gray-50 text-xs`}
                onMouseDown={() => {
                  // Geef hoofd-art mee + variant-info zodat caller de juiste prijs/code/combo kan zetten
                  onSelect(a, v || null);
                  setQ(displayCode || '');
                  setOpen(false);
                }}
                data-testid={`ref-zoeker-result-${idx}`}>
                <div className="truncate flex items-center gap-2">
                  {metFoto && <ZoekThumb art={a} />}
                  <span className="font-bold text-[#002FA7]">{displayCode}</span>
                  {v && <span className="px-1 py-0.5 bg-purple-100 text-purple-800 text-[9px] font-bold rounded">VAR</span>}
                  <span className="text-gray-600 truncate">{a.omschrijving}</span>
                  {comboStr && <span className="text-purple-600 font-mono text-[10px]">· {comboStr}</span>}
                  {a.eenheid === 'pack' && a.pack_grootte && <span className="text-gray-400 ml-1">({a.pack_grootte} st./doos)</span>}
                  {voorraad != null && <span className={`text-[10px] font-mono ml-1 ${voorraad < 5 ? 'text-amber-600 font-bold' : 'text-gray-400'}`}>· V:{voorraad}</span>}
                </div>
                <span className="font-bold text-[#002FA7] ml-3 shrink-0">{fmt(packPrijs)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Omschrijving zoeker (in line item) ──────────────────────────
function OmschrZoeker({ artikels, prijsGroep, onSelect, value, onChange, testId, metFoto }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 360 });
  const refEl = useRef(null);
  const inputEl = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (refEl.current && !refEl.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const computePos = () => {
    if (!inputEl.current) return;
    const r = inputEl.current.getBoundingClientRect();
    setPos({ top: r.bottom + 2, left: r.left, width: Math.max(360, r.width) });
  };
  useEffect(() => {
    if (!open) return;
    computePos();
    const onScroll = () => computePos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onScroll); };
  }, [open]);

  const q = value || '';
  const filtered = q.length >= 2 ? artikels.filter(a =>
    a.omschrijving?.toLowerCase().includes(q.toLowerCase()) || a.art_code?.toLowerCase().includes(q.toLowerCase())
  ).slice(0, 10) : [];

  return (
    <div className="relative" style={{ overflow: 'visible' }} ref={refEl}>
      <input
        ref={inputEl}
        className="field-input"
        style={{ padding: '4px 4px', fontSize: 12, width: '100%' }}
        placeholder="Vrije tekst of zoek artikel…"
        value={q}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => q.length >= 2 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        data-testid={testId}
      />
      {open && filtered.length > 0 && (
        <div className="bg-white border border-gray-200 shadow-xl max-h-60 overflow-y-auto" style={{ position: 'fixed', zIndex: 9999, top: pos.top, left: pos.left, minWidth: pos.width }} data-testid="omschr-dropdown">
          {filtered.map(a => {
            const packPrijs = lijnPrijsVan(a, a.prijzen?.[prijsGroep] || 0);
            return (
              <div key={a.id} className={`flex items-center justify-between px-3 ${metFoto ? 'py-1.5' : 'py-2'} hover:bg-[#F0F4FF] cursor-pointer border-b border-gray-50 text-xs`}
                onMouseDown={() => { onSelect(a); setOpen(false); }}>
                <div className="truncate flex items-center gap-2">
                  {metFoto && <ZoekThumb art={a} />}
                  <span className="font-bold text-[#002FA7] mr-1.5">{a.art_code}</span>
                  <span className="text-gray-600">{a.omschrijving}</span>
                  {a.eenheid === 'pack' && a.pack_grootte && <span className="text-gray-400 ml-1">({a.pack_grootte} st./doos)</span>}
                </div>
                <span className="font-bold text-[#002FA7] ml-3 shrink-0">{fmt(packPrijs)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────
export default function DocumentFormPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isSchema = location.pathname.startsWith('/schemas');
  const isEdit = !!id;
  const { user } = useAuth();
  const magWinst = user?.role === 'admin' || !!user?.mag_winst_zien;
  const [winstZichtbaar, setWinstZichtbaar] = useState(() => {
    if (searchParams.get('bron') === 'snel_scan') return false;
    try { return localStorage.getItem('docform_winst_zichtbaar') !== '0'; } catch { return true; }
  });
  const [bhModalOpen, setBhModalOpen] = useState(false);
  const [bhHistoriek, setBhHistoriek] = useState([]);
  const toggleWinstZichtbaar = () => setWinstZichtbaar(v => {
    const n = !v;
    try { localStorage.setItem('docform_winst_zichtbaar', n ? '1' : '0'); } catch { /* quota */ }
    return n;
  });

  // Schema-specifieke state
  const [schemaNaam, setSchemaNaam] = useState('');
  const [schemaStart, setSchemaStart] = useState(today());
  const [schemaEindeModus, setSchemaEindeModus] = useState('nooit'); // 'nooit' | 'datum' | 'aantal'
  const [schemaEinde, setSchemaEinde] = useState('');
  const [schemaAantalMax, setSchemaAantalMax] = useState('');
  const [schemaFreq, setSchemaFreq] = useState('maandelijks');
  const [schemaInterval, setSchemaInterval] = useState(1);
  const [schemaAutoSend, setSchemaAutoSend] = useState(false);
  const [schemaStatus, setSchemaStatus] = useState('actief');
  const [schemaAantalGedaan, setSchemaAantalGedaan] = useState(0);
  const [schemaVolgende, setSchemaVolgende] = useState('');

  const [klanten, setKlanten] = useState([]);
  const [artikels, setArtikels] = useState([]);
  const [artParams, setArtParams] = useState(null);
  const [inst, setInst] = useState({});

  const [type, setType] = useState(searchParams.get('type') || 'factuur');
  const [status, setStatus] = useState('concept');
  const [klantId, setKlantId] = useState(() => searchParams.get('klant_id') || '');
  // Offerte voor een niet-klant (prospect): vrije gegevens, firma verplicht — geen klantfiche nodig
  const [prospectMode, setProspectMode] = useState(false);
  const [prospect, setProspect] = useState({ firma: '', contact: '', straat: '', nr: '', bus: '', postcode: '', stad: '', land: 'België', email: '', tel: '', btw: '', taal: 'nl' });
  const [prospectBusy, setProspectBusy] = useState(false);
  const [datum, setDatum] = useState(today());
  const [termijn, setTermijn] = useState(30);
  const [eindeMaand, setEindeMaand] = useState(false);
  const [leverdatum, setLeverdatum] = useState(today());
  const [leverAdres, setLeverAdres] = useState(null);
  const klantGewisseldRef = useRef(false); // true = gebruiker wisselde bewust van klant → lijnen herprijzen
  const klantZoekerRef = useRef(null); // afwijkend leveradres voor dit document ({tav,straat,nr,bus,postcode,stad,land,opmerking})
  const [ref, setRef] = useState('');
  const [titel, setTitel] = useState('');
  const [nota, setNota] = useState('');
  const notaEdited = useRef(false);
  useEffect(() => { notaEdited.current = false; }, [id, isEdit, isSchema]);
  const [taal, setTaal] = useState('nl');
  const [lijnen, setLijnen] = useState(() => {
    // Pre-fill lijnen uit sessionStorage wanneer gestart vanuit Snel-Scanning
    const bron = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('bron') : null;
    if (bron === 'snel_scan') {
      try {
        const raw = sessionStorage.getItem('snel_scan_lijnen');
        if (raw) {
          sessionStorage.removeItem('snel_scan_lijnen');
          const data = JSON.parse(raw);
          if (Array.isArray(data) && data.length > 0) {
            return data.map(l => ({
              ...emptyLine(),
              ...l, // Overnemen: omschr, referentie, aantal, prijs, korting, btw, eenheid, eenheid_aantal, eenheid_label, toon_eenheid_prijs, ean, combo_key, variant_combo, variant_label
            }));
          }
        }
      } catch { /* stil falen, val terug op leeg */ }
    }
    return [emptyLine()];
  });
  const [btwRegel, setBtwRegel] = useState(null);
  const [btwRegime, setBtwRegime] = useState(''); // '', 'verlegd', 'icl', 'export', 'kleine_onderneming'
  const [financieleKorting, setFinancieleKorting] = useState('');
  // ─── Per-document betalingskorting (kassavermindering) ───
  // Standaardwaarden komen uit klant of globale instellingen, maar zijn op
  // document-niveau overschrijfbaar (% OF € — niet beide).
  const [betKortAan, setBetKortAan] = useState(false);
  const [betKortDagen, setBetKortDagen] = useState('');
  const [betKortPct, setBetKortPct] = useState('');
  const [betKortBedrag, setBetKortBedrag] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  // Track of er wijzigingen zijn die nog niet opgeslagen zijn — voorkomt verwarring bij PDF/e-mail
  const [dirty, setDirty] = useState(false);
  const goBack = usePageBackOptions({ dirty, busy: saving, fallback: isSchema ? '/herhaald' : documentListPath(type) });
  const [versturenOpen, setVersturenOpen] = useState(false);
  const [mailPreviewOpen, setMailPreviewOpen] = useState(false);
  const [verzendOpen, setVerzendOpen] = useState(false);
  const [klantAlerts, setKlantAlerts] = useState(null);
  const [klantSaldo, setKlantSaldo] = useState(null); // openstaand saldo voor deze klant
  const [klantNotities, setKlantNotities] = useState([]); // recente notities
  const [allEenheden, setAllEenheden] = useState([
    { value: 'st', label: 'st' }, { value: 'doos', label: 'doos' }, { value: 'pack', label: 'pack' }, { value: 'kg', label: 'kg' },
    { value: 'm', label: 'm' }, { value: 'l', label: 'l' }, { value: 'uur', label: 'uur' },
  ]);
  useEffect(() => {
    API.get('/eenheden').then(r => {
      const lst = (r.data || []).map(e => ({ value: e.value, label: e.value }));
      if (lst.length > 0) setAllEenheden(lst);
    }).catch(() => {});
  }, []);
  const [toonRecentContact, setToonRecentContact] = useState(() => localStorage.getItem('doc_toon_recent_contact') !== '0');
  const [notitieModal, setNotitieModal] = useState(null); // {type, prefill}
  const [prijsGroep, setPrijsGroep] = useState('basic');
  const [instLoaded, setInstLoaded] = useState(false);
  const [ogm, setOgm] = useState('');
  const [betalingen, setBetalingen] = useState([]);
  const [herinneringen, setHerinneringen] = useState([]);
  const [packing, setPacking] = useState(null); // {preview, sector}
  const [packingLoading, setPackingLoading] = useState(false);
  const [stroomRefresh, setStroomRefresh] = useState(0); // bump → DocumentenStroom herlaadt (bv. na factuur aanmaken vanuit leverbon)
  const [historiek, setHistoriek] = useState([]);
  const [showBetForm, setShowBetForm] = useState(false);
  const [betForm, setBetForm] = useState({ datum: today(), bedrag: '', betaalwijze: 'Bankverrichting', notities: '' });
  // Variant-picker voor documentregels — opent modal wanneer geselecteerd artikel varianten heeft
  const [variantPicker, setVariantPicker] = useState(null); // {artikel, lineIdx, preselectVariant?}
  // Variant-matrix voor documentregels — meerdere combinaties in 1 grid
  const [variantMatrix, setVariantMatrix] = useState(null); // {artikel, lineIdx}
  const toonLijnFotos = type === 'leverbon' || type === 'bestelbon'; // mini-foto's altijd op de lijnen
  const [lijnFotoPreview, setLijnFotoPreview] = useState(null); // {src, code, omschr}

  const verval = useMemo(() => calcVerval(datum, termijn, eindeMaand), [datum, termijn, eindeMaand]);

  useEffect(() => {
    API.get('/klanten').then(r => setKlanten(r.data)).catch(() => {});
    API.get('/artikels').then(r => setArtikels(r.data)).catch(() => {});
    API.get('/artikel-params').then(r => setArtParams(r.data)).catch(() => {});
    API.get('/instellingen').then(r => {
      const s = r.data;
      setInst(s);
      if (!isEdit) {
        const t = parseInt(s.betalingstermijn_std) || 30;
        setTermijn(TERMIJNEN.some(x => x.value === t) ? t : 30);
        if (s.termijn_einde_maand !== undefined) setEindeMaand(!!s.termijn_einde_maand);
      }
      setInstLoaded(true);
    }).catch(() => setInstLoaded(true));
    if (isEdit) {
      if (isSchema) {
        API.get(`/schemas/${id}`).then(r => {
          const s = r.data || {};
          setSchemaNaam(s.naam || '');
          setSchemaStart(s.start_datum || today());
          setSchemaFreq(s.freq || 'maandelijks');
          setSchemaInterval(parseInt(s.interval_aantal) || 1);
          setSchemaAutoSend(!!s.auto_versturen);
          setSchemaStatus(s.status || 'actief');
          setSchemaAantalGedaan(parseInt(s.aantal_gedaan) || 0);
          setSchemaVolgende(s.volgende || '');
          if (s.aantal_max) { setSchemaEindeModus('aantal'); setSchemaAantalMax(s.aantal_max); }
          else if (s.eind_datum) { setSchemaEindeModus('datum'); setSchemaEinde(s.eind_datum); }
          else { setSchemaEindeModus('nooit'); }
          const tpl = s.template_factuur || {};
          setType('factuur');
          setKlantId(tpl.klant_id || s.klant_id || '');
          setDatum(tpl.datum || today());
          setRef(tpl.ref || '');
          setTitel(tpl.titel || s.naam || '');
          setNota(tpl.nota || s.omschr || '');
          setTaal(tpl.taal || 'nl');
          setBtwRegel(tpl.btw_regel || null);
          if (tpl.termijn !== undefined) setTermijn(tpl.termijn);
          if (tpl.einde_maand !== undefined) setEindeMaand(tpl.einde_maand);
          if (tpl.lijnen?.length) {
            setLijnen(tpl.lijnen.map(l => ({ ...emptyLine(), ...l, ref_search: l.referentie || '' })));
          } else if (s.bedrag) {
            setLijnen([{ ...emptyLine(), omschr: s.omschr || s.naam || '', prijs: s.bedrag, btw: 21 }]);
          }
        }).catch(() => goBack());
      } else {
        API.get(`/documenten/${id}`).then(r => {
          const d = r.data;
          setType(d.type || 'factuur'); setKlantId(d.klant_id || '');
          if (!d.klant_id && d.prospect?.firma) { setProspectMode(true); setProspect(p => ({ ...p, ...d.prospect })); }
          setDatum(d.datum || today()); setRef(d.ref || ''); setTitel(d.titel || '');
          setNota(d.nota || ''); setStatus(d.status || 'concept');
          setLeverdatum(d.leverdatum || today()); setBtwRegel(d.btw_regel || null);
          setLeverAdres(d.levering_adres && typeof d.levering_adres === 'object' && (d.levering_adres.straat || d.levering_adres.stad) ? d.levering_adres : null);
          setBtwRegime(d.btw_regime || '');
          if (d.termijn !== undefined) setTermijn(d.termijn);
          if (d.einde_maand !== undefined) setEindeMaand(d.einde_maand);
          if (d.ogm) setOgm(d.ogm);
          setLijnen(d.lijnen?.length ? d.lijnen.map(l => ({ ...emptyLine(), ...l, ref_search: l.referentie || '' })) : [emptyLine()]);
          // Betalingskorting per document
          if (d.betalingskorting_actief) {
            setBetKortAan(true);
            setBetKortDagen(d.betalingskorting_dagen || '');
            setBetKortPct(d.betalingskorting_pct || '');
            setBetKortBedrag(d.betalingskorting_bedrag || '');
          }
        }).catch(() => goBack());
      }
    }
  }, [id, isEdit, isSchema, navigate]);

  useEffect(() => { if (!isEdit && TYPES[type]) setStatus(TYPES[type].statuses[0]); }, [type, isEdit]);

  // Load betalingen, herinneringen en historiek bij bewerken
  useEffect(() => {
    if (!isEdit || !id) return;
    API.get(`/documenten/${id}/betalingen`).then(r => setBetalingen(r.data?.betalingen || [])).catch(() => {});
    API.get('/herinneringen').then(r => setHerinneringen((r.data || []).filter(h => h.fac_id === id))).catch(() => {});
    API.get('/audit-log').then(r => setHistoriek((r.data || []).filter(a => a.entiteit_id === id).slice(-20))).catch(() => {});
  }, [isEdit, id]);

  // Historiek van de nieuwe Betalingsherinnering-module (PDF/e-mail/WhatsApp), specifiek voor deze factuur
  useEffect(() => {
    if (!isEdit || !id || !klantId || type !== 'factuur') return;
    API.get(`/klanten/${klantId}/betalingsherinneringen`)
      .then(r => setBhHistoriek((r.data || []).filter(h => (h.factuur_ids || []).includes(id))))
      .catch(() => {});
  }, [isEdit, id, klantId, type]);

  // Auto-fill default nota from instellingen based on type + taal
  useEffect(() => {
    if (isEdit || !instLoaded || !inst || notaEdited.current) return;
    const notaMap = {
      factuur: { nl: 'factuur_nota_nl', fr: 'factuur_nota_fr', en: 'factuur_nota_en' },
      proforma: { nl: 'proforma_nota_nl', fr: 'proforma_nota_fr', en: 'proforma_nota_en' },
      creditnota: { nl: 'cn_nota_nl', fr: 'cn_nota_fr', en: 'cn_nota_en' },
    };
    const key = notaMap[type]?.[taal];
    if (key && inst[key]) setNota(inst[key]);
    else if (!notaMap[type]) setNota('');
  }, [type, taal, instLoaded, isEdit, inst]);

  // Auto-set taal uit klant.taal bij klant-wissel (alleen voor nieuwe documenten)
  // Reden: respecteert de voorkeurtaal van elke klant zonder dat de user handmatig moet wisselen.
  // Edit-mode: behoud de oorspronkelijke taal van het bewaarde document.
  useEffect(() => {
    if (isEdit) return;
    if (!klantId || !klanten?.length) return;
    const k = klanten.find(x => x.id === klantId);
    const ktaal = (k?.taal || '').toLowerCase();
    if (['nl', 'fr', 'en', 'de'].includes(ktaal) && ktaal !== taal) {
      setTaal(ktaal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [klantId, klanten, isEdit]);

  // Dirty-state tracking: markeer als "niet-opgeslagen" bij wijzigingen na load
  // Skip initial mount (bewerk-mode: data pas na load beschikbaar)
  const initialLoadDone = useRef(false);
  // Real form interaction must not be swallowed by the initial hydration delay.
  // Only changes to the tracked document fields below mark the document dirty.
  const beginFormInteraction = () => { initialLoadDone.current = true; };
  useEffect(() => {
    if (!initialLoadDone.current) {
      // Eerste render — markeer als "gelezen" na een korte delay om initial-load-writes te negeren
      const t = setTimeout(() => { initialLoadDone.current = true; }, 600);
      return () => clearTimeout(t);
    }
    setDirty(true);
  }, [lijnen, klantId, datum, verval, ref, titel, nota, btwRegel, btwRegime, termijn, eindeMaand, leverdatum, financieleKorting]);

  useEffect(() => {
    if (btwRegel !== null) {
      const rule = BTW_REGELS.find(r => r.key === btwRegel);
      if (rule) setLijnen(prev => prev.map(l => ({ ...l, btw: rule.btw })));
    }
  }, [btwRegel]);

  // Klant-specifieke standaard BTW (≠21) — override wanneer geen BTW-regel actief is
  const klantStdBtw = useMemo(() => klantStandaardBtw(klanten.find(k => k.id === klantId)), [klanten, klantId]);

  // Pas klant-BTW toe op bestaande lijnen bij klantwissel (enkel nieuw document, geen actieve regel)
  useEffect(() => {
    if (isEdit || !klantId || btwRegel || klantStdBtw == null) return;
    setLijnen(prev => prev.map(l => !l.btw_handmatig && (l.art_id || l.omschr || l.prijs) ? { ...l, btw: klantStdBtw } : l));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [klantId, klantStdBtw, isEdit]);

  // ─── Automatische verzendkosten per land (Instellingen → Verzendkosten) ───
  const [verzendkostOnderdrukt, setVerzendkostOnderdrukt] = useState(false);
  useEffect(() => { setVerzendkostOnderdrukt(false); }, [klantId]);
  const verzendRegel = useMemo(() => {
    if (!inst?.verzendkosten_actief || !klantId) return null;
    const klant = klanten.find(k => k.id === klantId);
    if (!klant) return null;
    const iso = landNaarIso(klant.land || klant.country) || 'BE';
    const r = (inst.verzendkosten_regels || []).find(x => (x.land || '').toUpperCase() === iso);
    if (!r) return null;
    const kost = pf(r.kost); const drempel = pf(r.drempel);
    return (kost > 0 && drempel > 0) ? { land: iso, kost, drempel } : null;
  }, [inst, klantId, klanten]);
  useEffect(() => {
    if (isEdit || !instLoaded) return;
    const heeft = lijnen.some(l => l.is_verzendkost);
    if (!verzendRegel || verzendkostOnderdrukt) {
      if (heeft) setLijnen(prev => prev.filter(l => !l.is_verzendkost));
      return;
    }
    const goederen = lijnen.filter(l => !l.is_verzendkost).reduce((s, l) => s + lineTotal(l), 0);
    const moet = goederen > 0 && goederen < verzendRegel.drempel;
    if (moet && !heeft) {
      const vBtw = btwRegel ? (BTW_REGELS.find(r => r.key === btwRegel)?.btw ?? 21) : (klantStdBtw ?? 21);
      setLijnen(prev => {
        const line = { ...emptyLine(), omschr: 'Verzendkosten', aantal: 1, prijs: verzendRegel.kost, btw: vBtw, is_verzendkost: true };
        const idx = prev.findIndex(l => !l.art_id && !l.omschr && !l.prijs);
        if (idx >= 0) { const cp = [...prev]; cp.splice(idx, 0, line); return cp; }
        return [...prev, line];
      });
    } else if (!moet && heeft) {
      setLijnen(prev => prev.filter(l => !l.is_verzendkost));
    } else if (moet && heeft) {
      const cur = lijnen.find(l => l.is_verzendkost);
      if (cur && pf(cur.prijs) !== verzendRegel.kost) {
        setLijnen(prev => prev.map(l => l.is_verzendkost ? { ...l, prijs: verzendRegel.kost } : l));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lijnen, verzendRegel, verzendkostOnderdrukt, isEdit, instLoaded, btwRegel, klantStdBtw]);

  // Auto-detectie BTW-regel bij buitenlandse klant
  // Enkel bij een NIEUW document (niet in edit-mode) en wanneer er nog geen regel is ingesteld,
  // of wanneer we van een BE-klant naar een buitenlandse klant schakelen.
  const [lastAutoRegel, setLastAutoRegel] = useState(null);
  useEffect(() => {
    if (isEdit) return;
    if (!klantId) return;
    const klant = klanten.find(k => k.id === klantId);
    if (!klant) return;
    const autoKey = detecteerBtwRegel(klant);
    if (autoKey && autoKey !== btwRegel) {
      // Pas alleen toe als er nog geen regel is OF de vorige was ook een auto-regel
      if (!btwRegel || btwRegel === lastAutoRegel) {
        setBtwRegel(autoKey);
        setLastAutoRegel(autoKey);
      }
    } else if (!autoKey && lastAutoRegel && btwRegel === lastAutoRegel) {
      // Wissel terug naar BE → auto-regel opheffen
      setBtwRegel(null);
      setLastAutoRegel(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [klantId, klanten, isEdit]);

  // Haal VIES-alerts op zodra klant gekozen is (voor waarschuwingsbanner)
  useEffect(() => {
    if (!klantId) { setKlantAlerts(null); return; }
    API.get(`/klanten/${klantId}/alerts`, { params: { only_unread: true } })
      .then(r => setKlantAlerts(r.data))
      .catch(() => setKlantAlerts(null));
  }, [klantId]);

  // Prijsgroep volgt automatisch klant zodra een klant gekozen wordt (alleen bij nieuw doc)
  // De toegewezen klantgroep is leidend; PG:X is alleen een legacy fallback.
  useEffect(() => {
    // Bij een bestaand document enkel herprijzen als de gebruiker bewust de klant wisselde (niet bij laden)
    const gewisseld = klantGewisseldRef.current;
    if (isEdit && !gewisseld) return;
    klantGewisseldRef.current = false;
    if (!klantId) return;
    const klant = klanten.find(k => k.id === klantId);
    if (!klant) return;
    const klantPg = customerPriceGroup(klant, '');
    const pgEff = klantPg || prijsGroep;
    if (klantPg && klantPg !== prijsGroep) setPrijsGroep(klantPg);
    if ((klantPg && klantPg !== prijsGroep) || gewisseld) {
      setLijnen(prev => prev.map(l => {
        if (!l.art_id) return l;
        if (!gewisseld && (l.scan_prijs_handmatig || l.scan_prijs_precisie)) return l;
        const art = artikels.find(a => a.id === l.art_id);
        if (!art) return l;
        // Klantwissel: vaste nettoprijs / korting % van de NIEUWE klant toepassen (selectedKlant is al de nieuwe)
        const vast = gewisseld ? getKlantVastePrijs(art, pgEff) : null;
        const unitPrijs = art.prijzen?.[pgEff] || art.prijzen?.basic || 0;
        // Pack-aware (stuk × pack_grootte, of ingevoerde pakprijs bij prijs_basis='pack'); afgerond op 2 dec.
        const packPrijs = vast ?? lijnPrijsVan(art, unitPrijs);
        const lijnPrijs = l.eenheid === 'st' && Number(l.scan_stuks_per_pack) > 0 ? packPrijs / Number(l.scan_stuks_per_pack) : packPrijs;
        if (!gewisseld) return { ...l, prijs: lijnPrijs };
        return { ...l, prijs: lijnPrijs, korting: vast != null ? 0 : (getKlantKorting(art, pgEff) || 0) };
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [klantId, klanten, isEdit]);

  // Klant wisselen (ook op een opgeslagen document): bevestiging + herprijzen volgens de nieuwe klant
  const wisselKlant = (nieuwId) => {
    if (nieuwId && klantId && nieuwId !== klantId && isEdit) {
      const nk = klanten.find(k => k.id === nieuwId);
      const naam = nk ? (nk.bedrijf || `${nk.voorn || ''} ${nk.achtern || ''}`.trim()) : nieuwId;
      const heeftArtikelLijnen = lijnen.some(l => l.art_id);
      const msg = `Klant wijzigen naar "${naam}"?` + (heeftArtikelLijnen ? '\n\nPrijzen en kortingen van de lijnen worden herberekend volgens deze klant. Klik daarna op Bewaren.' : '\n\nKlik daarna op Bewaren.');
      if (!window.confirm(msg)) return;
      klantGewisseldRef.current = true;
    }
    setKlantId(nieuwId);
    if (nieuwId) setProspectMode(false);
    setDirty(true);
  };
  const prospectAan = () => { setProspectMode(true); setKlantId(''); setDirty(true); };
  const prospectUit = () => { setProspectMode(false); setDirty(true); };
  const prospectNaarKlant = async () => {
    if (!isEdit || !id) { alert('Sla de offerte eerst op'); return; }
    if (!window.confirm(`Klantfiche aanmaken voor "${prospect.firma}" en aan deze offerte koppelen?`)) return;
    setProspectBusy(true);
    try {
      const { data: res } = await API.post(`/documenten/${id}/prospect-naar-klant`, {});
      const r = await API.get('/klanten'); setKlanten(r.data);
      setKlantId(res.klant.id); setProspectMode(false); setDirty(false);
      toast.success(res.gekoppeld_bestaande ? `Bestaande klant "${res.klant.bedrijf}" gekoppeld` : `Klant "${res.klant.bedrijf}" aangemaakt en gekoppeld`);
    } catch (e) { alert('Klant aanmaken mislukt: ' + (e.response?.data?.detail || e.message)); }
    finally { setProspectBusy(false); }
  };

  // Haal openstaand saldo van klant op zodra hij geselecteerd is (zodat je weet of er nog openstaande facturen zijn)
  useEffect(() => {
    if (!klantId) { setKlantSaldo(null); return; }
    const params = { };
    if (isEdit && id) params.exclude_id = id;  // tel de huidige factuur (in edit-mode) niet mee
    API.get(`/klanten/${klantId}/saldo`, { params })
      .then(r => setKlantSaldo(r.data))
      .catch(() => setKlantSaldo(null));
  }, [klantId, isEdit, id]);

  // Recente notities + oproep-log voor deze klant (laatste 10)
  const loadNotities = (kid) => {
    API.get(`/klanten/${kid}/notities`, { params: { limit: 10 } })
      .then(r => setKlantNotities(r.data.items || []))
      .catch(() => setKlantNotities([]));
  };
  useEffect(() => {
    if (!klantId) { setKlantNotities([]); return; }
    loadNotities(klantId);
  }, [klantId]);

  const openNotitieModal = (type = 'telefoon') => {
    const openIds = (klantSaldo?.facturen || []).filter(f => f.is_vervallen).map(f => f.id);
    const prefill = type === 'telefoon' && openIds.length > 0
      ? `Gebeld i.v.m. vervallen factu${openIds.length > 1 ? 'ren' : 'ur'} ${openIds.join(', ')}. `
      : '';
    setNotitieModal({ type, datum: new Date().toISOString().slice(0, 16), omschr: prefill, factuur_refs: openIds });
  };

  const saveNotitie = async () => {
    if (!notitieModal || !klantId) return;
    if (!notitieModal.omschr.trim()) {
      alert('Vul een korte omschrijving in.');
      return;
    }
    try {
      if (notitieModal.id) {
        // Bewerken: PUT
        await API.put(`/klanten/${klantId}/notities/${notitieModal.id}`, {
          type: notitieModal.type,
          datum: new Date(notitieModal.datum).toISOString(),
          omschr: notitieModal.omschr,
          factuur_refs: notitieModal.factuur_refs || [],
        });
      } else {
        await API.post(`/klanten/${klantId}/notities`, {
          type: notitieModal.type,
          datum: new Date(notitieModal.datum).toISOString(),
          omschr: notitieModal.omschr,
          factuur_refs: notitieModal.factuur_refs || [],
        });
      }
      setNotitieModal(null);
      API.get(`/klanten/${klantId}/notities`, { params: { limit: 10 } })
        .then(r => setKlantNotities(r.data.items || []));
    } catch (e) {
      alert('Opslaan mislukt: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const deleteNotitie = async (nid) => {
    if (!klantId) return;
    if (!window.confirm('Deze notitie verwijderen?')) return;
    try {
      await API.delete(`/klanten/${klantId}/notities/${nid}`);
      API.get(`/klanten/${klantId}/notities`, { params: { limit: 10 } })
        .then(r => setKlantNotities(r.data.items || []));
    } catch (e) {
      alert('Verwijderen mislukt: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const editNotitie = (n) => {
    const d = new Date(n.datum);
    const localDt = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setNotitieModal({
      id: n.id,
      type: n.type,
      datum: localDt,
      omschr: n.omschr,
      factuur_refs: n.factuur_refs || [],
    });
  };

  const groepen = artParams?.groepen || [];
  const updLine = (i, k, v) => setLijnen(p => p.map((l, j) => {
    if (j !== i) return l;
    const next = { ...l, [k]: v };
    if (k === 'korting') { delete next.scan_korting_mode; delete next.scan_korting_euro; }
    return syncScanDocumentDiscount(next);
  }));
  const addLine = () => setLijnen(p => [...p, emptyLine()]);
  const rmLine = (i) => setLijnen(p => {
    if (p[i]?.is_verzendkost) setVerzendkostOnderdrukt(true);
    return p.filter((_, j) => j !== i);
  });

  // ── Barcode scanner ──────────────────────────────────────────
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanMsg, setScanMsg] = useState('');
  const handleScanned = (code) => {
    const clean = (code || '').trim();
    // 1) Zoek eerst op variant-EAN (artikel.varianten[i].ean)
    let matchArt = null;
    let matchVariant = null;
    for (const a of artikels) {
      if (a.varianten_actief && Array.isArray(a.varianten)) {
        const v = a.varianten.find(x => (x.ean || '').trim() === clean);
        if (v) { matchArt = a; matchVariant = v; break; }
      }
    }
    // 2) Als geen variant-hit → zoek op artikel-EAN (master)
    let art = matchArt || artikels.find(a => (a.ean || '').trim() === clean);
    if (!art) {
      setScanMsg(`Geen artikel of variant gevonden met EAN "${clean}".`);
      return true; // keep scanner open voor nieuwe poging
    }
    // 3) Als artikel varianten heeft maar geen variant-hit → open picker
    if (!matchVariant && art.varianten_actief && Array.isArray(art.varianten) && art.varianten.length > 0) {
      setScannerOpen(false);
      const emptyIdx = lijnen.findIndex(l => !l.art_id && !l.omschr && !l.prijs);
      const targetIdx = emptyIdx >= 0 ? emptyIdx : lijnen.length;
      if (emptyIdx < 0) setLijnen(prev => [...prev, emptyLine()]);
      // Open picker — gebruiker kiest combo
      setTimeout(() => setVariantPicker({ artikel: art, lineIdx: targetIdx }), 50);
      setScanMsg(`🎨 ${art.art_code} — kies variant`);
      return true;
    }

    art = artikelVoorVariant(art, matchVariant);
    const btwVal = btwRegel ? (BTW_REGELS.find(r => r.key === btwRegel)?.btw ?? 21) : (klantStdBtw ?? artikelBtw(art));
    const isPack = art.eenheid === 'pack' && art.pack_grootte > 0;
    const baseUnit = art.prijzen?.[prijsGroep] || 0;
    const unitPrijs = (matchVariant && matchVariant.prijs_override != null) ? parseFloat(matchVariant.prijs_override) : baseUnit;
    // Klantspecifieke vaste nettoprijs heeft voorrang op prijsgroep-prijs én korting %
    const vastePrijs = getKlantVastePrijs(art, undefined, matchVariant);
    // Pack-aware lijnprijs (stuk × pack_grootte, of ingevoerde pakprijs bij prijs_basis='pack'), 2 dec.
    const lijnPrijs = vastePrijs ?? lijnPrijsVan(art, unitPrijs);
    const heeftEenheid = (art.eenheid_aantal > 0) && (art.eenheid_label || '').trim();
    const packInfo = (isPack && !heeftEenheid && vastePrijs == null) ? `(${art.pack_grootte} st./doos @ ${fmt(stukPrijsVan(art, unitPrijs))}/st)` : '';
    const klantKort = vastePrijs != null ? '' : getKlantKorting(art, undefined, matchVariant);

    const variantLabel = matchVariant
      ? (art.dimensies || [])
          .map(d => matchVariant.combo?.[d.naam])
          .filter(Boolean)
          .join(' / ')
      : '';
    const variantRef = matchVariant ? (matchVariant.art_code || art.art_code || '') : (art.art_code || '');
    const variantEan = matchVariant ? (matchVariant.ean || '') : (art.ean || '');
    const extraOmschr = matchVariant && variantLabel ? ` · ${variantLabel}` : '';

    setLijnen(prev => {
      // Reeds bestaande regel met zelfde artikel+combo? → aantal +1 (kassa-workflow)
      const existingIdx = prev.findIndex(l => l.art_id === art.id && (l.combo_key || '') === (matchVariant?.combo_key || ''));
      if (existingIdx >= 0) {
        return prev.map((l, j) => j === existingIdx ? { ...l, aantal: pf(l.aantal) + 1 } : l);
      }
      // Anders: vul eerste lege regel, of voeg toe
      const emptyIdx = prev.findIndex(l => !l.art_id && !l.omschr && !l.prijs);
      const baseRows = emptyIdx >= 0 ? prev : [...prev, emptyLine()];
      const targetIdx = emptyIdx >= 0 ? emptyIdx : baseRows.length - 1;
      return baseRows.map((l, j) => j === targetIdx ? {
        ...l,
        aantal: 1,
        referentie: variantRef, ref_search: variantRef,
        omschr: `${art.omschrijving || ''}${extraOmschr}${packInfo ? ' ' + packInfo : ''}`,
        prijs: lijnPrijs, btw: btwVal, art_id: art.id,
        eenheid: art.eenheid === 'pack' ? 'pack' : (art.eenheid || 'st'),
        pack_info: packInfo,
        gewicht_kg: art.gewicht_kg || 0,
        volume_m3: art.volume_m3 || 0,
        korting: vastePrijs != null ? '' : (klantKort || l.korting || ''),
        ean: variantEan,
        toon_ean: !!art.toon_ean_op_document,
        eenheid_aantal: art.eenheid_aantal || 0,
        eenheid_label: art.eenheid_label || '',
        toon_eenheid_prijs: art.toon_eenheid_prijs !== false,
        pack_grootte: art.pack_grootte || 0,
        combo_key: matchVariant?.combo_key || '',
        variant_combo: matchVariant?.combo || null,
        variant_label: variantLabel,
      } : l);
    });
    setScanMsg(matchVariant
      ? `✓ ${variantRef} — ${art.omschrijving} · ${variantLabel}`
      : `✓ ${art.art_code} — ${art.omschrijving}`);
    return true; // continue scannen voor snelle bulk-invoer
  };

  // Klant-specifieke korting (prospect → pseudo-klant zodat e-mail/taal/adres gewoon werken)
  const selectedKlant = klanten.find(k => k.id === klantId) || (prospectMode && !klantId ? { bedrijf: prospect.firma, tav: prospect.contact, straat: prospect.straat, nr: prospect.nr, bus: prospect.bus, postcode: prospect.postcode, stad: prospect.stad, land: prospect.land, email: prospect.email, tel: prospect.tel, btwnr: prospect.btw, taal: prospect.taal, is_prospect: true } : {});
  const klantKortingen = selectedKlant.kortingen || [];

  // ─── Initialiseer financiële korting wanneer klant gekozen wordt ───
  // Op nieuw document: gebruik klant-defaults; op bestaand document: gebruik opgeslagen waarden
  useEffect(() => {
    if (isEdit) return; // bewerken: laad uit doc (gebeurt in laad-effect verderop)
    if (!klantId) {
      setBetKortAan(false); setBetKortDagen(''); setBetKortPct(''); setBetKortBedrag('');
      return;
    }
    if (selectedKlant.betalingskorting_actief) {
      setBetKortAan(true);
      setBetKortDagen(selectedKlant.betalingskorting_dagen || inst.betalingskorting_dagen || '');
      setBetKortPct(selectedKlant.betalingskorting_pct || inst.betalingskorting_pct || '');
      setBetKortBedrag(selectedKlant.betalingskorting_bedrag || '');
    } else {
      setBetKortAan(false);
      setBetKortDagen(''); setBetKortPct(''); setBetKortBedrag('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [klantId, selectedKlant.id]);

  const getKlantKorting = (art, pgOverride, variant) => customerDiscount(art, selectedKlant, pgOverride ?? prijsGroep, variant);

  // Klantspecifieke VASTE NETTOPRIJS (kolom "Vast bedrag" op klantenfiche, veld vast_bedrag).
  // De klant betaalt dit artikel altijd aan dit bedrag; heeft voorrang op korting %.
  // Voorrang: exact artikel > categorie > prijsgroep > alle artikels.
  const getKlantVastePrijs = (art, pgOverride, variant) => customerFixedPrice(art, selectedKlant, pgOverride ?? prijsGroep, variant);

  // Free text referentie (geen artikel geselecteerd)
  const handleFreeText = (i, text) => {
    updLine(i, 'referentie', text);
    updLine(i, 'ref_search', text);
  };

  // When article selected from ref search → fill line + apply klant korting
  // Als artikel varianten heeft → eerst VariantPicker openen (Optie A)
  const selectArtikelForLine = (i, art, variantOrSkip = false) => {
    // Als 2e arg een object is, is het een directe variant-keuze (bv. uit RefZoeker autocomplete) → skip picker
    if (variantOrSkip && typeof variantOrSkip === 'object') {
      _applyArtikelToLine(i, art, variantOrSkip);
      return;
    }
    const forceSkipVariantPicker = variantOrSkip === true;
    if (!forceSkipVariantPicker && art.varianten_actief && Array.isArray(art.varianten) && art.varianten.length > 0) {
      // Open variant-picker; pas regel toe na keuze via applyVariantToLine
      setVariantPicker({ artikel: art, lineIdx: i });
      return;
    }
    _applyArtikelToLine(i, art, null);
  };

  // Interne helper: past artikel (evt. met gekozen variant) toe op lijn
  const _applyArtikelToLine = (i, art, variant) => {
    art = artikelVoorVariant(art, variant);
    const btwVal = btwRegel ? (BTW_REGELS.find(r => r.key === btwRegel)?.btw ?? 21) : (klantStdBtw ?? artikelBtw(art));
    const isPack = art.eenheid === 'pack' && art.pack_grootte > 0;
    const baseUnit = art.prijzen?.[prijsGroep] || 0;
    // Variant prijs-override heeft voorrang
    const unitPrijs = (variant && variant.prijs_override != null) ? parseFloat(variant.prijs_override) : baseUnit;
    // Klantspecifieke vaste nettoprijs heeft voorrang op prijsgroep-prijs én korting %
    const vastePrijs = getKlantVastePrijs(art, undefined, variant);
    // Pack-aware lijnprijs (stuk × pack_grootte, of ingevoerde pakprijs bij prijs_basis='pack'), 2 dec.
    const lijnPrijs = vastePrijs ?? lijnPrijsVan(art, unitPrijs);
    const heeftEenheid = (art.eenheid_aantal > 0) && (art.eenheid_label || '').trim();
    const packInfo = (isPack && !heeftEenheid && vastePrijs == null) ? `(${art.pack_grootte} st./doos @ ${fmt(stukPrijsVan(art, unitPrijs))}/st)` : '';
    const klantKort = vastePrijs != null ? '' : getKlantKorting(art, undefined, variant);

    // Variant-specifieke velden
    const variantLabel = variant
      ? (art.dimensies || [])
          .map(d => variant.combo?.[d.naam])
          .filter(Boolean)
          .join(' / ')
      : '';
    const variantRef = variant ? (variant.art_code || art.art_code || '') : (art.art_code || '');
    const variantEan = variant ? (variant.ean || '') : (art.ean || '');
    const extraOmschr = variant && variantLabel ? ` · ${variantLabel}` : '';

    setLijnen(p => {
      const updated = p.map((l, j) => j === i ? {
        ...l,
        referentie: variantRef, ref_search: variantRef,
        omschr: `${art.omschrijving || ''}${extraOmschr}${packInfo ? ' ' + packInfo : ''}`,
        prijs: lijnPrijs, btw: btwVal, art_id: art.id,
        eenheid: art.eenheid === 'pack' ? 'pack' : (art.eenheid || 'st'),
        pack_info: packInfo,
        gewicht_kg: art.gewicht_kg || 0,
        volume_m3: art.volume_m3 || 0,
        korting: vastePrijs != null ? '' : (klantKort || l.korting || ''),
        ean: variantEan,
        toon_ean: !!art.toon_ean_op_document,
        eenheid_aantal: art.eenheid_aantal || 0,
        eenheid_label: art.eenheid_label || '',
        toon_eenheid_prijs: art.toon_eenheid_prijs !== false,
        pack_grootte: art.pack_grootte || 0,
        // Variant-metadata
        combo_key: variant?.combo_key || '',
        variant_combo: variant?.combo || null,
        variant_label: variantLabel,
      } : l);
      // 🪄 Auto-add nieuwe lege regel als dit de laatste regel was — geldt voor ALLE documenten
      if (i === p.length - 1) {
        updated.push(emptyLine());
      }
      return updated;
    });
  };

  // Callback na variant-keuze in modal
  const applyVariantToLine = (variant) => {
    if (!variantPicker) return;
    _applyArtikelToLine(variantPicker.lineIdx, variantPicker.artikel, variant);
    setVariantPicker(null);
  };

  // Callback na matrix-submit: vervangt of voegt toe na huidige regel
  // - Regel LEEG (geen art_id of geen aantal) → vervangen (happens from picker-switch)
  // - Regel VULD (al data) → nieuwe regels INVOEGEN NA deze regel (behoudt origineel)
  const applyMatrixToLines = (matrixLines) => {
    if (!variantMatrix || !matrixLines.length) return;
    const { artikel, lineIdx } = variantMatrix;
    const btwVal = btwRegel ? (BTW_REGELS.find(r => r.key === btwRegel)?.btw ?? 21) : (klantStdBtw ?? artikelBtw(artikel));
    const baseUnit = artikel.prijzen?.[prijsGroep] || 0;

    const buildLine = (mLine) => {
      const v = mLine.variant;
      const effective = artikelVoorVariant(artikel, v);
      const vastePrijsMatrix = getKlantVastePrijs(artikel, undefined, v);
      const klantKort = vastePrijsMatrix != null ? '' : getKlantKorting(artikel, undefined, v);
      const unitPrijs = vastePrijsMatrix ?? lijnPrijsVan(effective, (v && v.prijs_override != null) ? parseFloat(v.prijs_override) : baseUnit);
      const variantLabel = (artikel.dimensies || [])
        .map(d => v.combo?.[d.naam])
        .filter(Boolean)
        .join(' / ');
      return {
        ...emptyLine(),
        aantal: mLine.aantal,
        referentie: v.art_code || artikel.art_code || '',
        ref_search: v.art_code || artikel.art_code || '',
        omschr: `${artikel.omschrijving || ''} · ${variantLabel}`,
        prijs: unitPrijs,
        btw: btwVal,
        art_id: artikel.id,
        eenheid: artikel.eenheid === 'pack' ? 'pack' : (artikel.eenheid || 'st'),
        gewicht_kg: artikel.gewicht_kg || 0,
        volume_m3: artikel.volume_m3 || 0,
        korting: klantKort || '',
        ean: v.ean || '',
        toon_ean: !!artikel.toon_ean_op_document,
        eenheid_aantal: effective.eenheid_aantal || 0,
        eenheid_label: effective.eenheid_label || '',
        toon_eenheid_prijs: artikel.toon_eenheid_prijs !== false,
        pack_grootte: effective.pack_grootte || 0,
        combo_key: v.combo_key,
        variant_combo: v.combo,
        variant_label: variantLabel,
      };
    };

    const nieuweRegels = matrixLines.map(buildLine);
    setLijnen(prev => {
      const out = [...prev];
      const target = out[lineIdx];
      // Bepaal: regel leeg → vervangen; regel gevuld → invoegen NA
      const isLeeg = !target || (!target.art_id && !target.omschr && !target.prijs && (!target.aantal || pf(target.aantal) === 0));
      if (isLeeg) {
        out.splice(lineIdx, 1, ...nieuweRegels);
      } else {
        // Origineel behouden, nieuwe regels direct er onder
        out.splice(lineIdx + 1, 0, ...nieuweRegels);
      }
      // 🪄 Zorg dat er altijd een lege regel staat onderaan (vervolg-snel-typen)
      const last = out[out.length - 1];
      const lastLeeg = !last || (!last.art_id && !last.omschr && !last.prijs);
      if (!lastLeeg) {
        out.push(emptyLine());
      }
      return out;
    });
    toast.success(`${nieuweRegels.length} variant-regel(s) toegevoegd — vergeet niet op te slaan!`);
    setVariantMatrix(null);
  };

  const lineTotal = (l) => {
    l = syncScanDocumentDiscount(l);
    // Partial pack: als stuks_effectief < eenheid_aantal → prijs rato aangepast
    const ea = pf(l.eenheid_aantal);
    const se = pf(l.stuks_effectief);
    let effPrijs = pf(l.prijs);
    if (ea > 0 && se > 0 && se !== ea) {
      effPrijs = (pf(l.prijs) / ea) * se;
    }
    const base = pf(l.aantal) * effPrijs;
    const kortingPct = pf(l.korting);
    return base * (1 - kortingPct / 100);
  };
  const subtotaal = useMemo(() => lijnen.reduce((s, l) => s + lineTotal(l), 0), [lijnen]);
  const fKorting = pf(financieleKorting);
  const btwGroups = useMemo(() => {
    const g = {};
    lijnen.forEach(l => { const pct = pf(l.btw); g[pct] = (g[pct] || 0) + lineTotal(l); });
    return g;
  }, [lijnen]);
  const btwTotaal = useMemo(() => Object.entries(btwGroups).reduce((s, [p, b]) => s + b * (parseFloat(p) / 100), 0), [btwGroups]);
  const totaal = subtotaal - fKorting + btwTotaal;

  // 💰 Winst-berekening — live, per document (excl. BTW, gebaseerd op aankoopprijs artikels).
  // Zelfde formule als backend `/documenten-winst`: pack_grootte-multiplier + sanity-check.
  const winstInfo = useMemo(() => {
    if (!magWinst) return null;
    let omzet = 0, kost = 0;
    let ontbrekendeKosten = 0;
    const perRegel = [];
    lijnen.forEach((rawLine, idx) => {
      const l = syncScanDocumentDiscount(rawLine);
      const aantal = pf(l.aantal);
      const prijs = pf(l.prijs);
      const kortingPct = pf(l.korting);
      const nettoPrijs = kortingPct ? prijs * (1 - kortingPct / 100) : prijs;
      const omzetR = aantal * nettoPrijs;
      omzet += omzetR;
      if (!aantal || (!l.art_id && !l.omschr && !prijs) || scanAmountCorrection(l)) {
        perRegel[idx] = { omzet: omzetR, kost: 0, winst: omzetR }; return;
      }
      const art = l.art_id ? artikels.find(a => a.id === l.art_id) : null;
      let kostR = null;
      const aankoop = knownPurchasePrice(art);
      if (aankoop !== null) {
          let packMult = scanCostMultiplier(art, l);
          // Sanity-check: als pack-kost meer dan 2x verkoop is, terugvallen op 1x
          if (!l.scan_prijs_precisie && packMult > 1 && nettoPrijs > 0 && aankoop * packMult > nettoPrijs * 2) packMult = 1;
          kostR = aantal * aankoop * packMult;
          kost += kostR;
      } else ontbrekendeKosten += 1;
      perRegel[idx] = { omzet: omzetR, kost: kostR, winst: kostR !== null ? omzetR - kostR : null };
    });
    // Document-niveau financiële korting verlaagt de omzet (en dus de winst) ook
    omzet -= pf(financieleKorting);
    const winst = ontbrekendeKosten ? null : omzet - kost;
    return {
      omzet: Math.round(omzet * 100) / 100,
      kost: ontbrekendeKosten ? null : Math.round(kost * 100) / 100,
      winst: winst === null ? null : Math.round(winst * 100) / 100,
      marge_pct: winst !== null && omzet > 0 ? Math.round((winst / omzet) * 1000) / 10 : null,
      volledig: ontbrekendeKosten === 0,
      per_regel: perRegel,
    };
  }, [magWinst, lijnen, artikels, financieleKorting]);


  const addBetaling = async () => {
    const bedrag = parseFloat(betForm.bedrag) || 0;
    if (!bedrag) { alert('Vul een bedrag in'); return; }
    // Overbetaling-waarschuwing: indien bedrag > openstaand, vraag bevestiging
    const betaaldNu = betalingen.reduce((s, b) => s + Math.abs(b.bedrag || 0), 0);
    const openstaandNu = Math.max(0, totaal - betaaldNu);
    if (bedrag > openstaandNu + 0.01) {
      const over = bedrag - openstaandNu;
      const msg = openstaandNu < 0.01
        ? `Deze factuur staat al op € 0 — een extra betaling van ${fmt(bedrag)} maakt ze OVERBETAALD met ${fmt(over)}.\n\nWil je toch doorgaan?\n\nTip: Klik op het prullenbak-icoon naast een betaling om ze te verwijderen.`
        : `Bedrag (${fmt(bedrag)}) is méér dan het openstaande saldo (${fmt(openstaandNu)}).\nNa opslaan is de factuur OVERBETAALD met ${fmt(over)}.\n\nWil je toch doorgaan?`;
      if (!window.confirm(msg)) return;
    }
    try {
      await API.post('/bank', { datum: betForm.datum, bedrag, van: betForm.notities || '', meded: id, bron: betForm.betaalwijze || 'Manueel' });
      setShowBetForm(false);
      setBetForm({ datum: today(), bedrag: '', betaalwijze: 'Bankverrichting', notities: '' });
      // Refresh betalingen en status
      const bp = await API.get(`/documenten/${id}/betalingen`);
      setBetalingen(bp.data?.betalingen || []);
      const rd = await API.get(`/documenten/${id}`);
      if (rd.data?.status) setStatus(rd.data.status);
    } catch (e) { alert('Betaling toevoegen mislukt'); }
  };

  const delBetaling = async (b) => {
    if (!b?.id) return;
    const msg = `Betaling verwijderen?\n\n• Bedrag: ${fmt(Math.abs(b.bedrag || 0))}\n• Datum: ${b.datum}\n• Betaalwijze: ${b.bron || 'Bankverrichting'}\n\nDe factuurstatus wordt automatisch bijgewerkt.`;
    if (!window.confirm(msg)) return;
    try {
      await API.delete(`/bank/${b.id}`);
      const bp = await API.get(`/documenten/${id}/betalingen`);
      setBetalingen(bp.data?.betalingen || []);
      const rd = await API.get(`/documenten/${id}`);
      if (rd.data?.status) setStatus(rd.data.status);
    } catch (e) { alert('Verwijderen mislukt: ' + (e.response?.data?.detail || e.message)); }
  };

  // Dirty-guard: waarschuwt als niet-opgeslagen wijzigingen bestaan vóór PDF/Print/E-mail
  // Returns true als veilig om door te gaan.
  const guardDirty = async (actieLabel = 'deze actie') => {
    if (!dirty) return true;
    return window.confirm(
      `⚠️ Je hebt niet-opgeslagen wijzigingen.\n\n` +
      `Als je nu ${actieLabel} uitvoert, wordt de OUDE versie van het document gebruikt (zonder je recente aanpassingen).\n\n` +
      `• OK = Doorgaan met OUDE versie\n` +
      `• Annuleren = Eerst opslaan, dan opnieuw proberen`
    );
  };

  const save = async () => {
    if (lijnen.some(l => !scanDocumentDiscountValid(l))) {
      alert('De eurokorting mag niet hoger zijn dan het regeltotaal. Pas de korting of het aantal aan.'); return;
    }
    if (!klantId) {
      if (type === 'offerte' && prospectMode) {
        if (!(prospect.firma || '').trim()) { alert('Vul de firmanaam van de niet-klant in'); return; }
      } else { alert('Selecteer een klant'); return; }
    }
    if (selectedKlant?.geblokkeerd) {
      alert('🚫 Klant is geblokkeerd — document opslaan niet toegestaan.');
      return;
    }
    if (isSchema && !schemaNaam.trim()) { alert('Geef het schema een naam'); return; }
    setSaving(true);
    // Converteer lijnen met partial-pack: bij stuks_effectief < eenheid_aantal
    // passen we prijs rato aan (prijs × se/ea) voor correcte backend-totalen.
    const mapLijn = (l) => {
      const ea = pf(l.eenheid_aantal);
      const se = pf(l.stuks_effectief);
      let effPrijs = pf(l.prijs);
      let omschr = l.omschr;
      if (ea > 0 && se > 0 && se !== ea) {
        effPrijs = (pf(l.prijs) / ea) * se;
        if (omschr && !omschr.includes('uit ')) {
          omschr = `${omschr} (${se} ${l.eenheid_label || 'st'} uit ${ea})`;
        }
      }
      return syncScanDocumentDiscount({ ...l, aantal: pf(l.aantal), prijs: effPrijs, korting: pf(l.korting), omschr });
    };
    if (isSchema) {
      const template_factuur = {
        type: 'factuur', klant_id: klantId, datum, ref, titel, nota, taal,
        btw_regel: btwRegel, termijn, einde_maand: eindeMaand,
        lijnen: lijnen.map(mapLijn),
      };
      const schema = {
        naam: schemaNaam,
        klant_id: klantId,
        omschr: titel || nota || '',
        bedrag: totaal,
        freq: schemaFreq,
        interval_aantal: parseInt(schemaInterval) || 1,
        start_datum: schemaStart,
        volgende: schemaVolgende || schemaStart,
        eind_datum: schemaEindeModus === 'datum' ? schemaEinde : null,
        aantal_max: schemaEindeModus === 'aantal' ? parseInt(schemaAantalMax) || 0 : 0,
        auto_versturen: !!schemaAutoSend,
        status: schemaStatus,
        template_factuur,
      };
      try {
        if (isEdit) await API.put(`/schemas/${id}`, schema);
        else await API.post('/schemas', schema);
        navigate('/herhaald');
      } catch (e) { alert('Fout bij opslaan schema'); }
      setSaving(false);
      return;
    }
    const doc = {
      type, klant_id: klantId, datum, verval, ref, titel, nota, status,
      prospect: (!klantId && prospectMode && type === 'offerte') ? prospect : null,
      leverdatum, taal, btw_regel: btwRegel, btw_regime: btwRegime || null, termijn, einde_maand: eindeMaand, ogm,
      levering_adres: leverAdres || null,
      lijnen: lijnen.map(mapLijn),
      excl: subtotaal - fKorting, btw_totaal: btwTotaal, totaal, financiele_korting: fKorting,
      // Per-document betalingskorting (kassavermindering) — overschrijft klant-defaults
      betalingskorting_actief: !!betKortAan,
      betalingskorting_dagen: betKortAan ? (parseInt(betKortDagen) || 0) : 0,
      betalingskorting_pct: betKortAan && !betKortBedrag ? (parseFloat(betKortPct) || 0) : 0,
      betalingskorting_bedrag: betKortAan ? (parseFloat(betKortBedrag) || 0) : 0,
    };
    try {
      if (isEdit) {
        const { data: res } = await API.put(`/documenten/${id}`, doc);
        const kids = res?.kind_waarschuwingen || [];
        if (kids.length > 0) {
          const lijst = kids.map(k => `• ${k.type.toUpperCase()} ${k.id} (${k.status}) — ${Object.keys(k.verschillen).join(', ')}`).join('\n');
          const kindIds = kids.map(k => k.id);
          if (window.confirm(
            `Dit document heeft ${kids.length} afgeleid(e) document(en) die nu afwijk(en):\n\n${lijst}\n\n` +
            `Wil je deze automatisch mee bijwerken?\n\n` +
            `• OK = bijwerken (alleen concept-kinderen worden aangepast).\n` +
            `• Annuleren = wijzigingen alleen hier toepassen; controleer dan elk kind apart.`
          )) {
            try {
              await API.post(`/documenten/${id}/sync-kinderen`, { kind_ids: kindIds, include_sent: false });
            } catch (e) {
              alert('Synchroniseren naar kinderen is niet volledig gelukt: ' + (e.response?.data?.detail || e.message));
            }
          }
        }
        // Na opslaan bij bewerken: blijf op de pagina (niet terug naar overzicht)
        setSaving(false);
        setSaveMsg('✓ Document opgeslagen');
        setDirty(false);
        setTimeout(() => setSaveMsg(''), 2500);
      } else {
        // Nieuw document aangemaakt → navigeer naar bewerk-pagina van het nieuwe document
        // zodat de gebruiker kan verder werken (PDF, verzenden, betalen...)
        const { data: res } = await API.post('/documenten', doc);
        if (res?.id) {
          setSaving(false);
          setDirty(false);
          navigate(`/documenten/${res.id}/bewerk`, { replace: true });
          return; // Router can reuse this component on the edit route.
        }
        setSaving(false);
      }
    } catch (e) {
      const msg = e.response?.data?.detail;
      alert(msg ? `Fout bij opslaan:\n\n${msg}` : 'Fout bij opslaan');
      setSaving(false);
    }
  };

  const typeInfo = TYPES[type] || TYPES.factuur;

  return (
    <div data-testid="document-form-page" className="max-w-6xl mx-auto" onChangeCapture={beginFormInteraction} onClickCapture={beginFormInteraction}>
      <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-3">
        <h1 className="page-heading mb-0" style={{ fontSize: 'clamp(17px, 4.5vw, 22px)' }}>
          {isSchema
            ? (isEdit ? 'Herhaald schema bewerken' : 'Nieuw herhaald schema')
            : (isEdit ? `${typeInfo.label} bewerken` : `Nieuwe ${typeInfo.label.toLowerCase()}`)
          }
        </h1>
        {isEdit && !isSchema && (
          <div className="ml-auto flex flex-wrap justify-end gap-1.5 md:gap-2" data-testid="doc-action-bar">
            <button className="btn btn-secondary btn-sm" onClick={async () => {
              // Afdrukken: haal premium PDF van backend en open in print-window
              if (!(await guardDirty('afdrukken'))) return;
              try {
                const keuze = await vraagFotoKeuze(API, type);
                const prijs = await vraagPrijsKeuze(type);
                let url2 = `${process.env.REACT_APP_BACKEND_URL}/api/documenten/${id}/pdf?toon_fotos=${keuze}`;
                if (prijs !== null) url2 += `&met_prijzen=${prijs}`;
                const r = await fetch(url2, { credentials: 'include' });
                if (!r.ok) throw new Error(`PDF-generatie mislukt (${r.status})`);
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const w = window.open(url, '_blank');
                if (w) setTimeout(() => { try { w.print(); } catch {} }, 1000);
              } catch(e) { alert('Afdrukken mislukt: ' + e.message); }
            }} data-testid="action-afdrukken"><Printer size={14} /> Afdrukken</button>

            <RemotePrintButton documentId={id} documentType={type} bestandsnaam={`${id}.pdf`} label="Remote" testid="action-remote-print" />

            <button className="btn btn-secondary btn-sm" onClick={async () => {
              if (!klantId && !(prospectMode && prospect.email)) { alert(prospectMode ? 'Vul het e-mailadres van de niet-klant in' : 'Selecteer eerst een klant'); return; }
              if (!(await guardDirty('versturen'))) return;
              // Voor factuur/creditnota: open nieuwe modal met Peppol + E-mail keuze
              if (type === 'factuur' || type === 'creditnota') {
                setVersturenOpen(true);
                return;
              }
              if (!selectedKlant?.email) { alert('Klant heeft geen e-mailadres'); return; }
              // Voor andere doc-types: open MailPreviewModal voor controle vóór verzenden
              setMailPreviewOpen(true);
            }} data-testid="action-versturen"><EnvelopeSimple size={14} /> Versturen</button>

            {/* 💬 WhatsApp Quick-Send — open WhatsApp Hub met deze klant + factuur */}
            {klantId && id && (
              <a
                href={`/whatsapp?klant_id=${klantId}&factuur_id=${id}&template=${type === 'factuur' ? 'verzonden' : ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-sm inline-flex items-center gap-1 !bg-[#25D366] !text-white hover:!bg-[#1FAD55] !border-[#25D366]"
                title="Open WhatsApp Hub met deze klant + factuur al voor-geselecteerd"
                data-testid="action-whatsapp"
              >
                💬 WhatsApp
              </a>
            )}

            <button className="btn btn-secondary btn-sm" onClick={async () => {
              if (!(await guardDirty('downloaden'))) return;
              try {
                const rd = await API.get(`/documenten/${id}`);
                const d = rd.data;
                const k = klanten.find(x => x.id === d.klant_id) || {};
                const keuze = await vraagFotoKeuze(API, d.type || type);
                const prijs = await vraagPrijsKeuze(d.type || type);
                const params = { toon_fotos: keuze };
                if (prijs !== null) params.met_prijzen = prijs;
                // Centrale helper: gebruikt server-URL op iOS, blob elders
                const actions = await fetchPdfActions({
                  endpoint: `/documenten/${id}/pdf`,
                  label: pdfFilename(d, k).replace(/\.pdf$/, ''),
                  method: 'GET',
                  params,
                  filename: pdfFilename(d, k),
                });
                // iOS → open in viewer zodat gebruiker zelf kan delen/opslaan via native sheet
                // Desktop → direct download zoals voorheen
                if (isIOS()) openPdf(actions); else downloadPdf(actions);
              } catch(e) { alert('Downloaden mislukt: ' + e.message); }
            }} data-testid="action-downloaden"><DownloadSimple size={14} /> {isIOS() ? 'Openen' : 'Downloaden'}</button>

            {/* 📤 Delen via... — alleen op apparaten met Web Share API (iPhone/iPad/Android) */}
            <button className="btn btn-sm font-bold text-white" style={{ background: '#25D366', borderColor: '#25D366' }} onClick={async () => {
              if (!(await guardDirty('delen'))) return;
              try {
                const rd = await API.get(`/documenten/${id}`);
                const d = rd.data;
                const k = klanten.find(x => x.id === d.klant_id) || {};
                const keuze = await vraagFotoKeuze(API, d.type || type);
                const prijs = await vraagPrijsKeuze(d.type || type);
                const params = { toon_fotos: keuze };
                if (prijs !== null) params.met_prijzen = prijs;
                const actions = await fetchPdfActions({
                  endpoint: `/documenten/${id}/pdf`,
                  label: pdfFilename(d, k).replace(/\.pdf$/, ''),
                  method: 'GET',
                  params,
                  filename: pdfFilename(d, k),
                });
                await sharePdf(actions, { onError: (err) => alert('Delen mislukt: ' + err.message) });
              } catch(e) { alert('Delen mislukt: ' + e.message); }
            }} data-testid="action-delen" title="Deel PDF via WhatsApp / Mail / AirDrop">
              📤 Delen
            </button>

            {(type === 'factuur' || type === 'creditnota') && (
              <button className="btn btn-secondary btn-sm text-[#008A27]" onClick={() => {
                const betaaldTot = betalingen.reduce((s,b) => s + Math.abs(b.bedrag||0), 0);
                const openst = Math.max(0, totaal - betaaldTot);
                setShowBetForm(true);
                setBetForm(p => ({ ...p, bedrag: openst > 0 ? String(openst.toFixed(2)) : String(totaal.toFixed(2)), datum: today() }));
                setTimeout(() => {
                  const el = document.querySelector('[data-testid="betalingen-sectie"]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
              }} data-testid="action-betaling"><CurrencyEur size={14} weight="bold" /> Betaling</button>
            )}

            {type === 'factuur' && (
              <button
                className="btn btn-secondary btn-sm text-[#E5B300]"
                onClick={() => {
                  if (!klantId) { alert('Selecteer eerst een klant'); return; }
                  if (!id) { alert('Sla eerst de factuur op'); return; }
                  setBhModalOpen(true);
                }}
                data-testid="action-herinnering"
              >
                <Bell size={14} weight="bold" /> Betalingsherinnering
              </button>
            )}

            {/* Packing List — enkel voor factuur/bestelbon/leverbon */}
            {['factuur','bestelbon','leverbon','proforma'].includes(type) && (
              <button className="btn btn-secondary btn-sm" onClick={async () => {
                setPackingLoading(true);
                try {
                  const r = await API.post('/packing-from-invoice', { doc_id: id });
                  setPacking({ preview: r.data, sector: r.data.sector });
                } catch (e) {
                  alert('Packing list genereren mislukt: ' + (e.response?.data?.detail || e.message));
                } finally {
                  setPackingLoading(false);
                }
              }} data-testid="action-packing" disabled={packingLoading}>
                <Package size={14} weight="bold" /> {packingLoading ? 'Bezig...' : 'Packing List'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Type + Status pills */}
      {!isSchema && (
      <div className="flex gap-1 mb-3 flex-wrap items-center">
        {/* Actieve document-type: alleen deze badge tonen (geen wisselbalk tussen types).
            Creditnota kan enkel via factuur worden aangemaakt, niet rechtstreeks. */}
        {(() => {
          const colors = { offerte: '#7C3AED', bestelbon: '#2563EB', leverbon: '#059669', proforma: '#D97706', factuur: '#002FA7', creditnota: '#DC2626' };
          const c = colors[type] || '#6B7280';
          return (
            <div className="px-4 py-1.5 text-xs font-bold inline-flex items-center gap-2"
                 style={{ background: c, color: '#fff', borderBottom: `3px solid ${c}` }}
                 data-testid={`type-pill-${type}`}>
              {typeInfo.label}
              {type === 'creditnota' && <span className="opacity-80 text-[10px] font-normal">(vanuit factuur)</span>}
            </div>
          );
        })()}
        <div className="mx-2 border-l border-gray-200 h-6" />
        {typeInfo.statuses.map(s => {
          const sc = { concept:'#6B7280', openstaand:'#D97706', 'gedeeltelijk betaald':'#D97706', betaald:'#059669', vervallen:'#DC2626', verzonden:'#2563EB', goedgekeurd:'#059669', afgewezen:'#DC2626', besteld:'#059669', geleverd:'#059669' };
          return (<button key={s} onClick={() => setStatus(s)} className="px-3 py-1.5 text-xs font-medium transition-all"
            style={{ background: status === s ? sc[s] || '#6B7280' : '#F9FAFB', color: status === s ? '#fff' : '#9CA3AF', borderBottom: status === s ? `3px solid ${sc[s]}` : '3px solid transparent' }}
            data-testid={`status-pill-${s}`}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>);
        })}
      </div>
      )}

      {/* Herhaal-instellingen — enkel in schema-modus */}
      {isSchema && (
        <div className="section-card mb-4" data-testid="schema-herhaal-card">
          <div className="section-header flex items-center justify-between">
            <span className="flex items-center gap-2"><Repeat size={16} weight="bold" className="text-[#002FA7]" /> <span className="section-title">Herhaal-instellingen</span></span>
            <div className="flex items-center gap-1">
              {['actief', 'gepauzeerd', 'gestopt'].map(st => (
                <button key={st} onClick={() => setSchemaStatus(st)}
                  className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-all"
                  style={{
                    background: schemaStatus === st ? (st === 'actief' ? '#059669' : st === 'gepauzeerd' ? '#D97706' : '#6B7280') : '#F3F4F6',
                    color: schemaStatus === st ? '#fff' : '#9CA3AF',
                  }}
                  data-testid={`schema-status-${st}`}>{st}</button>
              ))}
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Naam van het schema *</label>
                <input className="field-input w-full" value={schemaNaam} onChange={e => setSchemaNaam(e.target.value)} placeholder="Bv. Hosting Klant BV — maandelijks" data-testid="schema-naam" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Startdatum *</label>
                <input type="date" className="field-input w-full" value={schemaStart} onChange={e => { setSchemaStart(e.target.value); if (!isEdit) setSchemaVolgende(e.target.value); }} data-testid="schema-start" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[120px_180px_1fr] gap-3 items-end">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Elke</label>
                <input type="number" min="1" className="field-input w-full" value={schemaInterval} onChange={e => setSchemaInterval(e.target.value)} data-testid="schema-interval" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Frequentie</label>
                <select className="field-input w-full" value={schemaFreq} onChange={e => setSchemaFreq(e.target.value)} data-testid="schema-freq">
                  <option value="wekelijks">Week(en)</option>
                  <option value="maandelijks">Maand(en)</option>
                  <option value="kwartaal">Kwartaal</option>
                  <option value="jaarlijks">Jaar</option>
                </select>
              </div>
              <div className="text-xs text-gray-500 pb-2">
                <Calendar size={12} weight="bold" className="inline mr-1 text-[#002FA7]" />
                Factuur wordt automatisch aangemaakt elke <strong>{schemaInterval || 1} {schemaFreq === 'wekelijks' ? 'week' + ((schemaInterval || 1) > 1 ? 'en' : '') : schemaFreq === 'maandelijks' ? 'maand' + ((schemaInterval || 1) > 1 ? 'en' : '') : schemaFreq === 'kwartaal' ? 'kwarta' + ((schemaInterval || 1) > 1 ? 'len' : 'al') : 'jaar' + ((schemaInterval || 1) > 1 ? 'en' : '')}</strong>, te starten op {schemaStart}.
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Einde</label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { k: 'nooit', l: 'Nooit (loopt verder)' },
                  { k: 'datum', l: 'Op vaste einddatum' },
                  { k: 'aantal', l: 'Na X facturen' },
                ].map(e => (
                  <button key={e.k} onClick={() => setSchemaEindeModus(e.k)}
                    className="px-3 py-1.5 text-xs font-semibold border transition-all"
                    style={{
                      background: schemaEindeModus === e.k ? '#002FA7' : '#fff',
                      color: schemaEindeModus === e.k ? '#fff' : '#6B7280',
                      borderColor: schemaEindeModus === e.k ? '#002FA7' : '#E5E7EB',
                    }}
                    data-testid={`schema-einde-${e.k}`}>{e.l}</button>
                ))}
              </div>
              {schemaEindeModus === 'datum' && (
                <div className="mt-2 max-w-xs">
                  <input type="date" className="field-input w-full" value={schemaEinde} onChange={e => setSchemaEinde(e.target.value)} data-testid="schema-einde-datum" />
                </div>
              )}
              {schemaEindeModus === 'aantal' && (
                <div className="mt-2 max-w-xs">
                  <input type="number" min="1" className="field-input w-full" value={schemaAantalMax} onChange={e => setSchemaAantalMax(e.target.value)} placeholder="Bv. 12" data-testid="schema-aantal-max" />
                  {isEdit && schemaAantalGedaan > 0 && <div className="text-[11px] text-gray-500 mt-1">Al <strong>{schemaAantalGedaan}</strong> facturen verwerkt.</div>}
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-gray-100">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={schemaAutoSend} onChange={e => setSchemaAutoSend(e.target.checked)} data-testid="schema-auto-send" />
                <span className="font-medium">Automatisch per e-mail versturen naar de klant</span>
                <span className="text-[11px] text-gray-400">(vereist SendGrid + e-mailadres bij klant)</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* ── Info-overzicht (alleen bij bewerken) ── */}
      {!isSchema && isEdit && (selectedKlant.id || (prospectMode && selectedKlant.is_prospect)) && (() => {
        const betaaldBedrag = betalingen.reduce((s,b) => s + Math.abs(b.bedrag||0), 0);
        const openstaandBedrag = Math.max(0, totaal - betaaldBedrag);
        const overbetaaldBedrag = Math.max(0, betaaldBedrag - totaal);
        const isOverdue = verval && new Date(verval) < new Date() && status !== 'betaald' && status !== 'concept' && status !== 'geannuleerd' && status !== 'geblokkeerd';
        const statusLabel = overbetaaldBedrag > 0.01 ? 'Overbetaald' : status === 'betaald' ? 'Betaald' : status === 'geannuleerd' ? 'Geannuleerd' : status === 'geblokkeerd' ? 'Geblokkeerd' : isOverdue ? 'Achterstallig' : status === 'gedeeltelijk betaald' ? 'Gedeeltelijk betaald' : status.charAt(0).toUpperCase() + status.slice(1);
        const statusColor = overbetaaldBedrag > 0.01 ? 'bg-purple-100 text-purple-800' : status === 'betaald' ? 'bg-green-100 text-green-800' : status === 'geannuleerd' ? 'bg-gray-200 text-gray-700 line-through' : status === 'geblokkeerd' ? 'bg-red-100 text-red-800' : isOverdue ? 'bg-red-100 text-red-800' : status === 'gedeeltelijk betaald' ? 'bg-yellow-100 text-yellow-800' : status === 'openstaand' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600';
        return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3" data-testid="doc-info-overview">
          {/* Klant */}
          <div className="bg-white border border-gray-200">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
              <span className="text-[10px] font-bold text-gray-400">Klant</span>
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  className="text-[10px] text-amber-700 font-bold hover:underline inline-flex items-center gap-0.5"
                  title="Andere klant kiezen voor dit document (prijzen/kortingen worden herberekend)"
                  onClick={() => { klantZoekerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => klantZoekerRef.current?.querySelector('[data-testid="klant-wijzig"], [data-testid="klant-zoek-input"]')?.click(), 350); }}
                  data-testid="klantblok-wijzig-klant"
                >
                  ✎ Wijzig klant
                </button>
                {selectedKlant.id ? <button className="text-[10px] text-[#002FA7] font-bold hover:underline" onClick={() => window.open(`/klanten?id=${selectedKlant.id}`, '_blank')}>Bekijk klant</button> : <span className="text-[10px] font-bold text-amber-700" data-testid="doc-info-prospect-badge">Niet-klant (prospect)</span>}
              </span>
            </div>
            <div className="px-3 py-2 text-xs space-y-0.5">
              <div className="font-bold">{selectedKlant.bedrijf || `${selectedKlant.voorn || ''} ${selectedKlant.achtern || ''}`.trim()}</div>
              {selectedKlant.straat && <div className="text-[10px] text-gray-500">{selectedKlant.straat} {selectedKlant.nr}</div>}
              {(selectedKlant.postcode || selectedKlant.stad) && <div className="text-[10px] text-gray-500">{selectedKlant.postcode} {selectedKlant.stad}</div>}
              {selectedKlant.land && <div className="text-[10px] text-gray-500">{selectedKlant.land}</div>}
              {selectedKlant.btwnr && <div className="text-[10px] text-gray-400 font-mono mt-1">{selectedKlant.btwnr}</div>}
              {/* LEVERADRES: doc-niveau (afwijkend voor dit document) > klant-default (lev_* ingevuld). Bij bestaand doc meteen bewaard. */}
              <LeverAdresBlok klant={selectedKlant} value={leverAdres} onChange={(v) => {
                setLeverAdres(v);
                if (isEdit) {
                  API.patch(`/documenten/${id}/levering-adres`, { levering_adres: v }).then(() => toast.success(v ? 'Leveradres bewaard' : 'Afwijkend leveradres verwijderd')).catch(() => setDirty(true));
                } else {
                  setDirty(true);
                }
              }} />
            </div>
          </div>

          {/* Factuur info */}
          <div className="bg-white border border-gray-200">
            <div className="px-3 py-2 border-b border-gray-100">
              <span className="text-[10px] font-bold text-gray-400">{typeInfo.label} info</span>
            </div>
            <div className="px-3 py-2 text-[11px] space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Status</span>
                <span className={`px-2 py-0.5 font-bold text-[10px] ${statusColor}`}>{statusLabel}</span>
              </div>
              {(type === 'factuur' || type === 'creditnota') && (
                <>
                  <div className="flex justify-between"><span className="text-gray-400">Factuurbedrag</span><span className="font-bold">{fmt(totaal)}</span></div>
                  {betaaldBedrag > 0 && <div className="flex justify-between"><span className="text-gray-400">Betaald</span><span className="font-bold text-[#008A27]">{fmt(betaaldBedrag)}</span></div>}
                  {openstaandBedrag > 0.01 && <div className="flex justify-between"><span className="text-gray-400">Openstaand</span><span className="font-bold text-[#E02424]">{fmt(openstaandBedrag)}</span></div>}
                  {overbetaaldBedrag > 0.01 && <div className="flex justify-between"><span className="text-gray-400">Overbetaald</span><span className="font-bold text-[#7C3AED]">{fmt(overbetaaldBedrag)}</span></div>}
                </>
              )}
              <div className="flex justify-between"><span className="text-gray-400">Datum</span><span>{datum}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Factuurnummer</span><span className="font-bold text-[#002FA7]">{id}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Vervaldatum</span><span>{verval}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Taal</span><span>{{ nl: 'Nederlands', fr: 'Frans', en: 'Engels', de: 'Duits' }[taal] || taal}</span></div>
            </div>
            {/* 💰 Winst-blok — enkel voor gebruikers met winst-machtiging, met oog-toggle */}
            {magWinst && winstInfo && (
              <div className="border-t border-gray-100 px-3 py-2 text-[11px] space-y-1 bg-gradient-to-b from-white to-[#FAFBFF]" data-testid="docform-winst-blok">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black tracking-wider uppercase text-gray-500">💰 Winst</span>
                  <button
                    type="button"
                    onClick={toggleWinstZichtbaar}
                    className="text-gray-400 hover:text-[#002FA7] transition-colors"
                    title={winstZichtbaar ? 'Verberg winst' : 'Toon winst'}
                    data-testid="toggle-winst-zichtbaar"
                  >
                    <Eye size={14} weight={winstZichtbaar ? 'fill' : 'regular'} />
                  </button>
                </div>
                {winstZichtbaar ? (
                  <>
                    <div className="flex justify-between"><span className="text-gray-400">Omzet (excl.)</span><span className="font-medium">{fmt(winstInfo.omzet)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Aankoopkost</span><span className="font-medium text-gray-700" data-testid="winst-aankoopkost">{winstInfo.kost === null ? 'Onbekend' : fmt(winstInfo.kost)}</span></div>
                    <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                      <span className="text-gray-500 font-bold">Winst</span>
                      <span className="flex items-center gap-1.5">
                        {winstInfo.winst === null ? <span className="font-bold text-amber-700" data-testid="winst-onbekend">Onbekend</span>
                          : <span className={`font-black ${winstInfo.winst >= 0 ? 'text-[#008A27]' : 'text-[#E02424]'}`} data-testid="winst-bedrag">{fmt(winstInfo.winst)}</span>}
                        {winstInfo.marge_pct !== null && (
                          <span className={`text-[10px] px-1.5 py-0.5 font-bold ${winstInfo.winst >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="winst-marge-pct">{winstInfo.marge_pct}%</span>
                        )}
                      </span>
                    </div>
                    {!winstInfo.volledig && (
                      <div className="text-[10px] text-amber-700 pt-0.5" data-testid="winst-inkoop-ontbreekt">Inkoopprijs ontbreekt — winst kan niet worden berekend.</div>
                    )}
                  </>
                ) : (
                  <div className="text-[10px] text-gray-400 italic">Winst verborgen — klik op het oog om te tonen</div>
                )}
              </div>
            )}
          </div>

          {/* Documentenstroom */}
          {isEdit && (
            <DocumentenStroom docId={id} klantId={klantId} refreshKey={stroomRefresh} onChange={() => { /* trigger re-render if needed */ }} />
          )}

          {/* Verzend-controle: check of dit doc overeenstemt met brondocument */}
          {isEdit && (
            <ControlePanel docId={id} klanten={klanten} />
          )}

          {/* Omzetten naar ander type */}
          {isEdit && (
            <ConversieKnoppen huidigType={type} docId={id} docStatus={status} onConverted={() => setStroomRefresh(k => k + 1)} />
          )}

          {/* Leverbon handtekening + Sendcloud verzendlabel in één card */}
          {isEdit && type === 'leverbon' && (
            <HandtekeningPanel docId={id} klantNaam={selectedKlant?.naam || ''} inst={inst} onVerzend={() => setVerzendOpen(true)} />
          )}

          {/* Foto's van de artikels op dit document (leverbon/bestelbon) */}
          {(type === 'leverbon' || type === 'bestelbon') && (
            <ArtikelFotosPanel lijnen={lijnen} artikels={artikels} />
          )}
        </div>
        );
      })()}

      {/* Lightbox voor mini-foto op een lijn */}
      {lijnFotoPreview && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={() => setLijnFotoPreview(null)} data-testid="line-foto-lightbox">
          <div className="max-w-3xl w-full flex flex-col items-center gap-3" onClick={e => e.stopPropagation()}>
            <img src={lijnFotoPreview.src} alt={lijnFotoPreview.code} className="max-h-[75vh] max-w-full object-contain rounded shadow-2xl bg-white" />
            <div className="text-white text-center">
              <div className="font-mono font-bold text-lg">{lijnFotoPreview.code}</div>
              <div className="text-sm text-white/80">{lijnFotoPreview.omschr}</div>
            </div>
            <button type="button" className="text-white/80 hover:text-white text-sm underline" onClick={() => setLijnFotoPreview(null)}>Sluiten</button>
          </div>
        </div>
      )}

      {/* Nieuw document (nog geen info-overzicht): artikelfoto's als strook boven het formulier */}
      {!isSchema && !isEdit && (type === 'leverbon' || type === 'bestelbon') && (
        <ArtikelFotosPanel lijnen={lijnen} artikels={artikels} compact />
      )}

      {/* Form card */}
      <div className="mb-2" style={{ borderTop: '3px solid #002FA7', background: '#fff', border: '1px solid #E5E5E5', borderTopWidth: 3, borderTopColor: '#002FA7' }}>
        <div className="p-4">
          {/* GROTE rode banner: klant geblokkeerd → document-opmaak/wijziging niet toegestaan */}
          {selectedKlant?.geblokkeerd && (
            <div
              className="mb-3 px-4 py-3 border-2 border-red-500 bg-red-50 text-red-900 rounded-lg flex items-start gap-3"
              data-testid="klant-geblokkeerd-banner"
            >
              <span className="text-2xl shrink-0 leading-none">🚫</span>
              <div className="flex-1">
                <div className="font-bold text-base">Klant is geblokkeerd</div>
                <div className="text-xs mt-0.5 leading-relaxed">
                  <strong>{selectedKlant.bedrijf || `${selectedKlant.voorn || ''} ${selectedKlant.achtern || ''}`.trim() || selectedKlant.naam}</strong> staat op blokkade.
                  Document-opmaak, opslaan of wijzigen is niet toegestaan zolang de blokkade actief is.
                  {selectedKlant.geblokkeerd_reden && (
                    <span className="block mt-1 italic">Reden: {selectedKlant.geblokkeerd_reden}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="text-xs font-bold text-red-800 hover:text-red-900 underline mt-1"
                  onClick={() => window.open(`/klanten?id=${selectedKlant.id}`, '_blank')}
                  data-testid="klant-geblokkeerd-naar-klant"
                >
                  → Bekijk klant om blokkade te beheren
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-start gap-3 md:gap-4 mb-3 pb-3" style={{ borderBottom: '1px solid #F3F4F6' }}>
            {!prospectMode && <div ref={klantZoekerRef}><KlantZoeker klanten={klanten} klantId={klantId} onChange={wisselKlant} /></div>}
            {type === 'offerte' && !prospectMode && !klantId && (
              <button type="button" className="btn btn-secondary btn-sm self-center" onClick={prospectAan} title="Offerte opmaken voor iemand die nog geen klant is — enkel de firmanaam is verplicht" data-testid="offerte-niet-klant">
                Niet-klant / prospect
              </button>
            )}
            {prospectMode && (
              <div className="w-full border border-amber-300 bg-amber-50/60 p-3" data-testid="prospect-blok">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="text-xs font-bold text-amber-900 uppercase tracking-wide">Offerte voor niet-klant (prospect)</div>
                  <div className="flex items-center gap-2">
                    {isEdit && id && (
                      <button type="button" className="text-[11px] font-bold px-2 py-1 bg-[#002FA7] text-white hover:bg-[#00247f] disabled:opacity-50" disabled={prospectBusy} onClick={prospectNaarKlant} data-testid="prospect-naar-klant">
                        {prospectBusy ? 'Bezig…' : '+ Klant aanmaken uit deze gegevens'}
                      </button>
                    )}
                    <button type="button" className="text-[11px] text-gray-600 underline" onClick={prospectUit} data-testid="prospect-annuleer">Toch een klant kiezen</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                  <div className="col-span-2"><label className="form-label">Firmanaam <span className="text-red-600">*</span></label><input className="field-input" value={prospect.firma} onChange={e => { setProspect(p => ({ ...p, firma: e.target.value })); setDirty(true); }} placeholder="Verplicht" data-testid="prospect-firma" /></div>
                  <div className="col-span-2"><label className="form-label">Contactpersoon</label><input className="field-input" value={prospect.contact} onChange={e => { setProspect(p => ({ ...p, contact: e.target.value })); setDirty(true); }} data-testid="prospect-contact" /></div>
                  <div><label className="form-label">BTW-nr</label><input className="field-input" value={prospect.btw} onChange={e => { setProspect(p => ({ ...p, btw: e.target.value })); setDirty(true); }} placeholder="BE0…" data-testid="prospect-btw" /></div>
                  <div><label className="form-label">Taal</label><select className="field-input" value={prospect.taal} onChange={e => { setProspect(p => ({ ...p, taal: e.target.value })); setDirty(true); }} data-testid="prospect-taal"><option value="nl">NL</option><option value="fr">FR</option><option value="en">EN</option><option value="de">DE</option></select></div>
                  <div className="col-span-2"><label className="form-label">Straat</label><input className="field-input" value={prospect.straat} onChange={e => { setProspect(p => ({ ...p, straat: e.target.value })); setDirty(true); }} data-testid="prospect-straat" /></div>
                  <div><label className="form-label">Nr / bus</label><div className="flex gap-1"><input className="field-input" value={prospect.nr} onChange={e => { setProspect(p => ({ ...p, nr: e.target.value })); setDirty(true); }} data-testid="prospect-nr" /><input className="field-input w-14" value={prospect.bus} onChange={e => { setProspect(p => ({ ...p, bus: e.target.value })); setDirty(true); }} placeholder="bus" data-testid="prospect-bus" /></div></div>
                  <div><label className="form-label">Postcode</label><input className="field-input" value={prospect.postcode} onChange={e => { setProspect(p => ({ ...p, postcode: e.target.value })); setDirty(true); }} data-testid="prospect-postcode" /></div>
                  <div><label className="form-label">Gemeente</label><input className="field-input" value={prospect.stad} onChange={e => { setProspect(p => ({ ...p, stad: e.target.value })); setDirty(true); }} data-testid="prospect-stad" /></div>
                  <div><label className="form-label">Land</label><input className="field-input" value={prospect.land} onChange={e => { setProspect(p => ({ ...p, land: e.target.value })); setDirty(true); }} data-testid="prospect-land" /></div>
                  <div className="col-span-2 md:col-span-3"><label className="form-label">E-mail</label><input type="email" className="field-input" value={prospect.email} onChange={e => { setProspect(p => ({ ...p, email: e.target.value })); setDirty(true); }} placeholder="voor het versturen van de offerte" data-testid="prospect-email" /></div>
                  <div className="col-span-2 md:col-span-3"><label className="form-label">Telefoon</label><input className="field-input" value={prospect.tel} onChange={e => { setProspect(p => ({ ...p, tel: e.target.value })); setDirty(true); }} data-testid="prospect-tel" /></div>
                </div>
                <div className="text-[10px] text-amber-800 mt-2">Koopt de prospect? Klik dan op "Klant aanmaken uit deze gegevens" (of bij het omzetten naar bestelbon/factuur wordt dit automatisch voorgesteld).</div>
              </div>
            )}
            {klantKortingen.length > 0 && (
              <div className="shrink-0 max-w-full px-3 py-1.5 bg-green-50 border border-green-200 text-xs text-green-700 font-medium" data-testid="klant-korting-badge">
                Korting: {klantKortingen.map(k => {
                  const vast = parseFloat(k.vast_bedrag);
                  const deel = (Number.isFinite(vast) && vast > 0) ? `vaste prijs € ${vast}` : `${k.percentage}%`;
                  if (!k.artikel) return `Alles ${deel}`;
                  const lower = k.artikel.toLowerCase();
                  const clean = lower.startsWith('cat:') ? k.artikel.slice(4) : lower.startsWith('pg:') ? k.artikel.slice(3) : k.artikel;
                  const tag = lower.startsWith('cat:') ? 'CAT' : lower.startsWith('pg:') ? 'PG' : 'ART';
                  return `${tag} ${clean} ${deel}`;
                }).join(' | ')}
                {selectedKlant.kortinggroep && <span className="ml-1 text-green-500">({selectedKlant.kortinggroep})</span>}
              </div>
            )}
          </div>

          {/* VIES-wijziging waarschuwing */}
          {klantAlerts && klantAlerts.count > 0 && (
            <div
              className={`mb-3 px-4 py-3 border-l-4 ${klantAlerts.has_kritiek ? 'bg-red-50 border-red-500 text-red-900' : 'bg-amber-50 border-amber-500 text-amber-900'}`}
              data-testid="klant-vies-warning"
            >
              <div className="flex items-start gap-3">
                <span className="text-lg shrink-0">{klantAlerts.has_kritiek ? '⚠️' : '🔔'}</span>
                <div className="flex-1 text-xs">
                  <div className="font-bold mb-1">
                    {klantAlerts.has_kritiek
                      ? 'Kritieke afwijking op klantgegevens'
                      : `Niet-bevestigde wijziging${klantAlerts.count === 1 ? '' : 'en'} (${klantAlerts.count})`}
                  </div>
                  <ul className="space-y-0.5">
                    {klantAlerts.items.slice(0, 3).map(a => (
                      <li key={a.id} className="flex items-center gap-2">
                        <span className="font-semibold uppercase text-[10px] tracking-wider">{a.veld}</span>
                        <span className="text-gray-500 line-through text-[11px]">{a.oud || '—'}</span>
                        <span className="text-[11px]">→</span>
                        <span className={`font-semibold text-[11px] ${a.kritiek ? 'text-red-700' : ''}`}>{a.nieuw}</span>
                      </li>
                    ))}
                    {klantAlerts.items.length > 3 && (
                      <li className="text-[10px] italic opacity-60">+{klantAlerts.items.length - 3} meer…</li>
                    )}
                  </ul>
                  <div className="mt-2">
                    <button
                      type="button"
                      className="text-[11px] font-bold underline hover:opacity-70"
                      onClick={() => navigate('/notificaties')}
                      data-testid="klant-vies-warning-link"
                    >
                      Bekijk en bevestig in notificaties →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Openstaand saldo klant — altijd zichtbaar bij factuur/proforma/creditnota zodra klant geselecteerd is */}
          {klantSaldo && klantSaldo.aantal_openstaand > 0 && (
            <div
              className={`mb-3 px-4 py-3 border-l-4 ${klantSaldo.aantal_vervallen > 0 ? 'bg-red-50 border-red-500' : 'bg-amber-50 border-amber-500'}`}
              data-testid="klant-saldo-banner"
            >
              <div className="flex items-start gap-3">
                <span className="text-lg shrink-0">{klantSaldo.aantal_vervallen > 0 ? '🚨' : '💶'}</span>
                <div className="flex-1 text-xs">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className={`font-bold ${klantSaldo.aantal_vervallen > 0 ? 'text-red-900' : 'text-amber-900'}`}>
                      Openstaand saldo bij deze klant: <span className="font-mono">{fmt(klantSaldo.openstaand_totaal)}</span>
                      {klantSaldo.aantal_vervallen > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white text-[10px] uppercase tracking-wider">
                          {klantSaldo.aantal_vervallen}× vervallen · {fmt(klantSaldo.vervallen_totaal)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {selectedKlant?.tel && (
                        <a
                          href={`tel:${selectedKlant.tel}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold bg-white border border-gray-300 hover:bg-gray-50"
                          title={`Bellen: ${selectedKlant.tel}`}
                          data-testid="klant-bellen-link"
                        >
                          📞 {selectedKlant.tel}
                        </a>
                      )}
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold bg-[#002FA7] text-white hover:bg-[#001f6e]"
                        onClick={() => openNotitieModal('telefoon')}
                        data-testid="log-gesprek-btn"
                      >
                        ✎ Log gesprek
                      </button>
                    </div>
                  </div>
                  <div className="text-[11px] text-gray-700 mb-1.5">
                    {klantSaldo.aantal_openstaand} onbetaalde {klantSaldo.aantal_openstaand > 1 ? 'facturen' : 'factuur'} — oudste vervallen eerst:
                  </div>
                  <ul className="space-y-0.5">
                    {klantSaldo.facturen.slice(0, 4).map(f => {
                      const dagenTxt = f.dagen_over_verval > 0
                        ? `${f.dagen_over_verval}d te laat`
                        : f.dagen_over_verval === 0
                          ? 'verval vandaag'
                          : `nog ${Math.abs(f.dagen_over_verval)}d`;
                      return (
                        <li key={f.id} className="flex items-center gap-2 text-[11px]">
                          <button
                            type="button"
                            className="font-mono font-bold text-[#002FA7] hover:underline"
                            onClick={() => window.open(`/documenten/${f.id}/bewerk`, '_blank')}
                          >
                            {f.id}
                          </button>
                          <span className="text-gray-500">{f.datum}</span>
                          <span className={`${f.is_vervallen ? 'text-red-700 font-bold' : 'text-gray-600'}`}>{dagenTxt}</span>
                          <span className="ml-auto font-mono font-bold">{fmt(f.openstaand)}</span>
                        </li>
                      );
                    })}
                    {klantSaldo.facturen.length > 4 && (
                      <li className="text-[10px] italic opacity-60">+{klantSaldo.facturen.length - 4} meer…</li>
                    )}
                    {klantSaldo.heeft_meer && (
                      <li className="text-[10px] italic opacity-60">(top 10 getoond — er zijn nog meer openstaande)</li>
                    )}
                  </ul>
                  {/* Recente contact-log: laatste 3 gesprekken — aan/uit toggle om lijst kort te houden */}
                  {klantNotities.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-300/50">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
                          Recent contact ({klantNotities.length})
                        </div>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer text-[10px] text-gray-600 select-none">
                          <input
                            type="checkbox"
                            checked={toonRecentContact}
                            onChange={e => {
                              setToonRecentContact(e.target.checked);
                              localStorage.setItem('doc_toon_recent_contact', e.target.checked ? '1' : '0');
                            }}
                            data-testid="toggle-recent-contact"
                            className="w-3 h-3 cursor-pointer"
                          />
                          <span>Tonen</span>
                        </label>
                      </div>
                      {toonRecentContact && (
                        <ul className="space-y-0.5">
                          {klantNotities.slice(0, 3).map(n => {
                            const d = new Date(n.datum);
                            const dagen = Math.floor((Date.now() - d.getTime()) / 86400000);
                            const dagenTxt = dagen === 0 ? 'vandaag' : dagen === 1 ? 'gisteren' : `${dagen}d geleden`;
                            const icon = n.type === 'telefoon' ? '📞' : n.type === 'email' ? '✉️' : n.type === 'afspraak' ? '📅' : '📝';
                            return (
                              <li key={n.id} className="group flex items-start gap-2 text-[11px] hover:bg-white/40 px-1 py-0.5">
                                <span className="shrink-0">{icon}</span>
                                <span className="text-gray-500 shrink-0 w-20">{dagenTxt}</span>
                                <span className="text-gray-800 flex-1">{n.omschr}</span>
                                <span className="shrink-0 flex items-center gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
                                  <button
                                    type="button"
                                    className="text-[#002FA7] hover:bg-blue-50 px-1"
                                    onClick={() => editNotitie(n)}
                                    title="Bewerken"
                                    data-testid={`edit-notitie-${n.id}`}
                                  >✎</button>
                                  <button
                                    type="button"
                                    className="text-red-500 hover:bg-red-50 px-1"
                                    onClick={() => deleteNotitie(n.id)}
                                    title="Verwijderen"
                                    data-testid={`del-notitie-${n.id}`}
                                  >×</button>
                                </span>
                              </li>
                            );
                          })}
                          {klantNotities.length > 3 && (
                            <li className="text-[10px] italic opacity-60">+{klantNotities.length - 3} oudere contacten</li>
                          )}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Notitie / Gesprek-log modal */}
          {notitieModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setNotitieModal(null)}>
              <div className="w-full max-w-lg bg-white shadow-2xl" onClick={e => e.stopPropagation()} data-testid="notitie-modal">
                <div className="px-5 py-3 border-b border-gray-200 bg-[#002FA7] text-white flex items-center justify-between">
                  <h3 className="text-sm font-bold">✎ {notitieModal.id ? 'Contact bewerken' : 'Contact registreren'} bij {selectedKlant?.bedrijf || selectedKlant?.naam || 'klant'}</h3>
                  <button onClick={() => setNotitieModal(null)} className="text-white hover:opacity-70">✕</button>
                </div>
                <div className="p-5 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wider block mb-1">Type</label>
                      <select
                        className="field-input text-sm w-full"
                        value={notitieModal.type}
                        onChange={e => setNotitieModal(p => ({ ...p, type: e.target.value }))}
                        data-testid="notitie-type"
                      >
                        <option value="telefoon">📞 Telefoon</option>
                        <option value="email">✉️ E-mail</option>
                        <option value="afspraak">📅 Afspraak</option>
                        <option value="notitie">📝 Notitie</option>
                        <option value="overig">• Overig</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wider block mb-1">Datum/uur</label>
                      <input
                        type="datetime-local"
                        className="field-input text-sm w-full"
                        value={notitieModal.datum}
                        onChange={e => setNotitieModal(p => ({ ...p, datum: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wider block mb-1">
                      Wat werd besproken?
                    </label>
                    <textarea
                      className="field-input text-sm w-full"
                      rows={5}
                      placeholder="Bv. Klant belooft factuur te betalen voor eind week. Graag herinneren op vrijdag."
                      value={notitieModal.omschr}
                      onChange={e => setNotitieModal(p => ({ ...p, omschr: e.target.value }))}
                      data-testid="notitie-omschr"
                    />
                  </div>
                  {notitieModal.factuur_refs && notitieModal.factuur_refs.length > 0 && (
                    <div className="text-[11px] text-gray-600 flex items-center gap-2">
                      <span className="font-bold">Gekoppelde facturen:</span>
                      {notitieModal.factuur_refs.map(r => (
                        <span key={r} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 font-mono text-[10px]">{r}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
                  <button className="btn btn-secondary btn-sm" onClick={() => setNotitieModal(null)}>Annuleren</button>
                  <button className="btn btn-primary btn-sm" onClick={saveNotitie} data-testid="notitie-save">
                    Opslaan
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid gap-x-6 gap-y-2" style={{ gridTemplateColumns: '130px 1fr 130px 1fr', alignItems: 'center' }}>
            <label className="text-xs text-gray-500 text-right font-medium">Datum</label>
            <input type="date" className="field-input text-sm" style={{ padding: '6px 10px' }} value={datum} onChange={e => setDatum(e.target.value)} data-testid="doc-datum" />
            <label className="text-xs text-gray-500 text-right font-medium">Documentnummer</label>
            <input className="field-input text-sm" style={{ padding: '6px 10px', background: '#F9FAFB', color: '#9CA3AF' }} value="(auto)" disabled />

            <label className="text-xs text-gray-500 text-right font-medium">Betalingstermijn</label>
            <div className="flex items-center gap-3">
              <select className="field-input text-sm" style={{ padding: '6px 10px', width: 140 }} value={termijn} onChange={e => setTermijn(parseInt(e.target.value))} data-testid="doc-termijn">
                {TERMIJNEN.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <span className="text-xs px-2 py-1" style={{ background: '#FEF3C7', color: '#92400E', fontWeight: 600 }}>Verval: {verval}</span>
            </div>
            <label className="text-xs text-gray-500 text-right font-medium">Leverdatum</label>
            <input type="date" className="field-input text-sm" style={{ padding: '6px 10px' }} value={leverdatum} onChange={e => setLeverdatum(e.target.value)} data-testid="doc-leverdatum" />

            {/* BTW-regime — wettelijk verplicht voor correcte verplichte vermeldingen op PDF en UBL */}
            {(type === 'factuur' || type === 'creditnota') && (
              <>
                <label className="text-xs text-gray-500 text-right font-medium">BTW-regime</label>
                <select
                  className="field-input text-sm"
                  style={{ padding: '6px 10px' }}
                  value={btwRegime}
                  onChange={e => setBtwRegime(e.target.value)}
                  data-testid="doc-btw-regime"
                  title="Wettelijke BTW-regime — bepaalt verplichte vermeldingen op PDF en UBL-XML (KB nr. 1 art. 5)"
                >
                  <option value="">Standaard (met BTW)</option>
                  <option value="verlegd">BTW verlegd (medecontractant — art. 20 KB nr. 1)</option>
                  <option value="icl">Intracommunautaire levering (art. 39bis WBTW)</option>
                  <option value="export">Uitvoer buiten EU (art. 39 WBTW)</option>
                  <option value="kleine_onderneming">Vrijstelling kleine ondernemingen (art. 56bis)</option>
                </select>
              </>
            )}

            <label className="text-xs text-gray-500 text-right font-medium">PO / referentie</label>
            <input className="field-input text-sm" style={{ padding: '6px 10px' }} value={ref} onChange={e => setRef(e.target.value)} data-testid="doc-ref" />
            <label className="text-xs text-gray-500 text-right font-medium">Taal</label>
            <select className="field-input text-sm" style={{ padding: '6px 10px' }} value={taal} onChange={e => setTaal(e.target.value)} data-testid="doc-taal">
              <option value="nl">Nederlands</option><option value="fr">Frans</option><option value="en">Engels</option><option value="de">Duits</option>
            </select>

            <label className="text-xs text-gray-500 text-right font-medium"></label>
            <label className="flex items-center gap-2 cursor-pointer">
              <div className={`w-9 h-[18px] rounded-full relative transition-colors ${eindeMaand ? 'bg-[#002FA7]' : 'bg-gray-300'}`}
                onClick={() => setEindeMaand(!eindeMaand)} data-testid="doc-einde-maand">
                <div className={`w-4 h-4 bg-white rounded-full absolute top-[1px] transition-all shadow-sm ${eindeMaand ? 'left-[19px]' : 'left-[1px]'}`} />
              </div>
              <span className="text-xs text-gray-500">Einde maand</span>
            </label>

            {(type === 'factuur' || type === 'creditnota') && inst.ogm_actief !== false && <>
              <label className="text-xs text-gray-500 text-right font-medium">Mededeling (OGM)</label>
              <div className="flex items-center gap-2">
                <input className="field-input text-sm font-mono font-bold" style={{ padding: '6px 10px', background: ogm ? '#F0FDF4' : '#F9FAFB', color: ogm ? '#166534' : '#9CA3AF', letterSpacing: '0.5px' }}
                  value={ogm || '(wordt automatisch gegenereerd)'} disabled data-testid="doc-ogm" />
              </div>
            </>}
          </div>
        </div>
      </div>

      {/* Titel + prijsgroep */}
      <div className="flex gap-2 mb-2">
        <input className="field-input flex-1 text-sm" placeholder="Titel (optioneel)" value={titel} onChange={e => setTitel(e.target.value)} data-testid="doc-titel" style={{ padding: '6px 10px' }} />
        {groepen.length > 0 && (
          <select className="field-input text-xs font-semibold" style={{ width: 110, padding: '4px 8px', borderColor: groepen.find(g => g.key === prijsGroep)?.color || '#002FA7', color: groepen.find(g => g.key === prijsGroep)?.color || '#002FA7' }}
            value={prijsGroep} onChange={e => {
              const nieuwPg = e.target.value;
              setPrijsGroep(nieuwPg);
              // Herprijs alle regels die aan een artikel gekoppeld zijn (respecteer klant-vaste-prijs)
              setLijnen(prev => prev.map(l => {
                if (!l.art_id) return l;
                const art = artikels.find(a => a.id === l.art_id);
                if (!art) return l;
                const vast = getKlantVastePrijs(art);
                if (vast != null) return l; // klant heeft vaste netto-prijs → niet aanraken
                const baseUnit = art.prijzen?.[nieuwPg] || art.prijzen?.basic || 0;
                const packPrijs = lijnPrijsVan(art, baseUnit);
                const lijnPrijs = l.eenheid === 'st' && Number(l.scan_stuks_per_pack) > 0 ? packPrijs / Number(l.scan_stuks_per_pack) : packPrijs;
                return { ...l, prijs: lijnPrijs };
              }));
            }} data-testid="prijs-groep-select">
            {groepen.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        )}
      </div>

      {/* Line items - REF is now searchable */}
      <div className="mb-2" style={{ background: '#fff', border: '1px solid #E5E5E5', position: 'relative', overflow: 'visible' }}>
        <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
          <table className="data-table" style={{ fontSize: 12, overflow: 'visible' }}>
            <thead>
              <tr style={{ background: '#F0F4FF' }}>
                {toonLijnFotos && <th style={{ width: 36, padding: '8px 2px', color: '#002FA7', textAlign: 'center' }} title="Artikelfoto">📷</th>}
                <th style={{ width: 145, padding: '8px 6px', color: '#002FA7' }}>Ref. / Artikel</th>
                <th style={{ width: 36, padding: '8px 2px', color: '#002FA7', textAlign: 'center' }} title="Matrix-invoer voor varianten">⊞</th>
                <th style={{ width: 130, padding: '8px 6px', color: '#002FA7' }}>Aantal × Pack</th>
                <th style={{ padding: '8px 6px', color: '#002FA7' }}>Omschrijving</th>
                <th style={{ width: 60, padding: '8px 4px', color: '#002FA7' }}>Enh</th>
                <th style={{ width: 60, padding: '8px 6px', color: '#002FA7' }}>Korting</th>
                <th style={{ width: 20, padding: '8px 2px' }}></th>
                <th style={{ width: 55, padding: '8px 4px', color: '#002FA7' }}>BTW</th>
                <th style={{ width: 100, padding: '8px 6px', color: '#002FA7' }}>Prijs/enh</th>
                <th style={{ width: 95, padding: '8px 6px', color: '#002FA7' }} className="right">Totaal</th>
                <th style={{ width: 24, padding: '8px 2px' }}></th>
              </tr>
            </thead>
            <tbody>
              {lijnen.map((rawLine, i) => {
                const l = syncScanDocumentDiscount(rawLine);
                const lt = lineTotal(l);
                // Sectie-header regel (bv. "Leverbon LB-XXXX") volledig breed, niet editable
                if (l.is_header) {
                  return (
                    <tr key={i} style={{ background: '#F5F3FF', borderTop: '2px solid #7C3AED' }}>
                      <td colSpan={toonLijnFotos ? 11 : 10} style={{ padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#5B21B6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        📦 {l.omschr}
                      </td>
                      <td style={{ padding: '3px 0', textAlign: 'right' }}>
                        <button className="text-red-300 hover:text-red-600 p-0.5" onClick={() => rmLine(i)} data-testid={`rm-line-${i}`} title="Sectie-header verwijderen"><Trash size={12} /></button>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={i}>
                    {toonLijnFotos && (
                      <td style={{ padding: '3px 2px', verticalAlign: 'middle' }}>
                        <LijnFoto artikel={l.art_id ? artikels.find(a => a.id === l.art_id) : null} onOpen={setLijnFotoPreview} />
                      </td>
                    )}
                    <td style={{ padding: '3px 3px', position: 'relative' }}>
                      <RefZoeker artikels={artikels} prijsGroep={prijsGroep} btwOverride={btwRegel ? BTW_REGELS.find(r => r.key === btwRegel)?.btw : klantStdBtw} metFoto={toonLijnFotos}
                        currentRef={l.ref_search || l.referentie || ''} onSelect={(art, variant) => selectArtikelForLine(i, art, variant)} onFreeText={(txt) => handleFreeText(i, txt)} />
                      {/* Variant-badge blijft als overlay rechtsboven (neemt geen plaats in) */}
                      {l.combo_key && l.variant_label && (
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-5 h-5 bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 rounded-sm"
                          style={{ position: 'absolute', top: 2, right: 4 }}
                          onClick={() => {
                            const art = artikels.find(a => a.id === l.art_id);
                            if (art && art.varianten_actief) {
                              setVariantPicker({ artikel: art, lineIdx: i, preselectVariant: { combo_key: l.combo_key, combo: l.variant_combo } });
                            }
                          }}
                          title={`Variant: ${l.variant_label} — klik om te wijzigen`}
                          data-testid={`line-variant-badge-${i}`}
                        >
                          <span style={{ fontSize: 9 }}>🎨</span>
                        </button>
                      )}
                    </td>
                    {/* Eigen Matrix-kolom — opent matrix-invoer voor varianten */}
                    <td style={{ padding: '3px 2px', textAlign: 'center' }}>
                      {l.art_id && (() => {
                        const art = artikels.find(a => a.id === l.art_id);
                        if (!art?.varianten_actief || !(art.varianten || []).length) return null;
                        return (
                          <button
                            type="button"
                            className="inline-flex items-center justify-center w-7 h-7 bg-purple-50 border border-purple-200 text-purple-700 hover:bg-purple-100 rounded-sm font-bold"
                            onClick={() => setVariantMatrix({ artikel: art, lineIdx: i })}
                            title="Open matrix-invoer voor meerdere varianten"
                            data-testid={`line-variant-matrix-${i}`}
                          >
                            <span style={{ fontSize: 14 }}>⊞</span>
                          </button>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '3px 3px' }}>
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          className="flex items-center justify-center w-5 h-6 text-gray-500 hover:text-white hover:bg-[#002FA7] border border-gray-300 rounded-sm text-sm font-bold leading-none select-none"
                          onClick={() => {
                            const cur = parseFloat(l.aantal) || 0;
                            const next = Math.max(0, cur - 1);
                            updLine(i, 'aantal', String(next));
                          }}
                          title="Aantal verlagen"
                          data-testid={`line-aantal-minus-${i}`}
                        >−</button>
                        <input type="text" inputMode="decimal" className="field-input text-center no-spinner" style={{ padding: '4px 2px', fontSize: 12, flex: 1, minWidth: 32 }} value={fmtDec(l.aantal)} onChange={e => updLine(i, 'aantal', e.target.value.replace(',', '.'))} placeholder="1" data-testid={`line-aantal-${i}`} />
                        <button
                          type="button"
                          className="flex items-center justify-center w-5 h-6 text-gray-500 hover:text-white hover:bg-[#002FA7] border border-gray-300 rounded-sm text-sm font-bold leading-none select-none"
                          onClick={() => {
                            const cur = parseFloat(l.aantal) || 0;
                            updLine(i, 'aantal', String(cur + 1));
                          }}
                          title="Aantal verhogen"
                          data-testid={`line-aantal-plus-${i}`}
                        >+</button>
                        {parseFloat(l.eenheid_aantal || 0) > 0 && (
                          <>
                            <span className="text-[10px] text-gray-400 font-bold ml-0.5">×</span>
                            <input type="text" inputMode="decimal" className="field-input text-center no-spinner" style={{ padding: '4px 2px', fontSize: 11, flex: 1, minWidth: 40, color: '#6D28D9' }}
                                   value={parseFloat(l.stuks_effectief || 0) > 0 ? fmtDec(l.stuks_effectief) : fmtDec(l.eenheid_aantal)}
                                   onChange={e => updLine(i, 'stuks_effectief', e.target.value.replace(',', '.'))}
                                   title={`Aantal ${l.eenheid_label || 'st'} per ${l.eenheid || 'pack'} (partial pack — max ${l.eenheid_aantal})`}
                                   data-testid={`line-stuks-eff-${i}`} />
                          </>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '3px 3px' }}>
                      <OmschrZoeker
                        artikels={artikels}
                        prijsGroep={prijsGroep}
                        value={l.omschr}
                        onChange={(v) => updLine(i, 'omschr', v)}
                        onSelect={(art) => selectArtikelForLine(i, art)}
                        testId={`line-omschr-${i}`}
                        metFoto={toonLijnFotos}
                      />
                      {eenheidSuffix(l) && (
                        <div className="text-[10px] text-amber-700 font-mono mt-0.5" data-testid={`line-eenheid-prijs-${i}`}>
                          {eenheidSuffix(l).trim()}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '3px 2px' }}><select className="field-input" style={{ padding: '3px 0px', fontSize: 10 }} value={l.eenheid || 'st'} onChange={e => updLine(i, 'eenheid', e.target.value)} data-testid={`line-eenheid-${i}`}>{allEenheden.map(u => <option key={u.value} value={u.value}>{u.value}</option>)}</select></td>
                    <td style={{ padding: '3px 3px' }}><input type="text" inputMode="decimal" className="field-input text-right" style={{ padding: '4px 2px', fontSize: 11 }} value={fmtDec(l.korting || '')} onChange={e => updLine(i, 'korting', e.target.value.replace(',', '.'))} data-testid={`line-korting-${i}`} />{l.scan_korting_mode === 'euro' && <span className="block text-[9px] text-gray-500 text-right" data-testid={`line-euro-discount-${i}`}>{fmt(l.scan_korting_euro)} / regel</span>}</td>
                    <td className="text-[10px] text-gray-300 text-center" style={{ padding: '3px 0' }}>%</td>
                    <td style={{ padding: '3px 2px' }}><DocumentVatSelect value={l.btw} onChange={value => updLine(i, 'btw', value)} testId={`document-vat-${i}`} /></td>
                    <td style={{ padding: '3px 3px' }}><input type="text" inputMode="decimal" className="field-input text-right" style={{ padding: '4px 3px', fontSize: 12 }} placeholder="0,00" value={fmtDec(l.prijs)} onChange={e => updLine(i, 'prijs', e.target.value.replace(',', '.'))} data-testid={`line-prijs-${i}`} /></td>
                    <td className="right font-bold" style={{ padding: '3px 6px', fontSize: 12 }}>{fmt(lt)}</td>
                    <td style={{ padding: '3px 0' }}><button className="text-red-300 hover:text-red-600 p-0.5" onClick={() => rmLine(i)} data-testid={`rm-line-${i}`}><Trash size={12} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-1.5 border-t border-gray-100 flex justify-between items-center" style={{ background: '#FAFBFC' }}>
          <div className="flex items-center gap-3">
            <button className="text-[#002FA7] text-xs font-semibold hover:underline" onClick={addLine} data-testid="add-line-btn"><Plus size={10} className="inline mr-0.5" />Regel toevoegen</button>
            {verzendRegel && lijnen.some(l => l.is_verzendkost) && (
              <span className="text-[10px] px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-900" data-testid="verzendkost-banner">
                🚚 Verzendkosten € {fmtDec(verzendRegel.kost)} automatisch toegevoegd (onder € {fmtDec(verzendRegel.drempel)} — {verzendRegel.land})
              </span>
            )}
            <button
              type="button"
              className="text-[#002FA7] text-xs font-semibold hover:underline inline-flex items-center gap-1"
              onClick={() => { setScanMsg(''); setScannerOpen(true); }}
              data-testid="scan-barcode-btn"
              title="Barcode scannen om artikel automatisch toe te voegen"
            >
              <Barcode size={13} /> Barcode scannen
            </button>
            {scanMsg && <span className={`text-[11px] ${scanMsg.startsWith('✓') ? 'text-green-700' : 'text-amber-700'} font-medium`} data-testid="scan-msg">{scanMsg}</span>}
          </div>
          <span className="text-[10px] text-gray-400 italic">Prijzen excl. BTW</span>
        </div>
      </div>

      {/* BTW + Totals side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 mb-3">
        <div className="lg:col-span-3" style={{ background: '#FAFBFC', border: '1px solid #E5E5E5' }}>
          <div className="px-4 py-2 border-b border-gray-200" style={{ background: '#F3F4F6' }}>
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">BTW-regeling</span>
          </div>
          {/* Auto-detect banner wanneer BTW-regel werd toegepast obv klantland */}
          {(() => {
            const kl = klanten.find(k => k.id === klantId);
            const autoKey = detecteerBtwRegel(kl);
            if (!autoKey || btwRegel !== autoKey) return null;
            const iso = landNaarIso(kl?.land || kl?.country);
            const reden = autoKey === 'intracommunautair'
              ? `Klant in EU-land ${iso} met BTW-nummer → intracommunautaire levering (0% verleggen, art. 39bis W.BTW)`
              : autoKey === 'export_eu'
                ? `Klant buiten EU (${iso}) → uitvoer (0% export, art. 39 W.BTW)`
                : '';
            return (
              <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900 flex items-start gap-2" data-testid="btw-auto-banner">
                <span className="shrink-0">🌍</span>
                <div>
                  <strong>Automatisch toegepast:</strong> {reden}. U kunt deze regel altijd manueel uitschakelen door op de toggle hieronder te klikken.
                </div>
              </div>
            );
          })()}
          {/* Export-herinnering: uitvoerbewijs (EX1/EAD) bewaren */}
          {btwRegel === 'export_eu' && (type === 'factuur' || type === 'creditnota') && (
            <div className="px-3 py-2 bg-rose-50 border-b border-rose-200 text-[11px] text-rose-900 flex items-start gap-2" data-testid="export-herinnering">
              <span className="shrink-0">📦</span>
              <div>
                <strong>Let op — uitvoerbewijs vereist:</strong> Bij uitvoer buiten de EU moet u een douane-uitvoerbewijs (EX1 / EAD / DAU) kunnen voorleggen als bewijs voor de 0%-BTW-vrijstelling. Bewaar dit document <strong>minimaal 7 jaar</strong>. Zonder geldig uitvoerbewijs kan de BTW bij controle alsnog worden opgeëist (art. 39 §1 W.BTW).
              </div>
            </div>
          )}
          <div className="p-3 space-y-1.5">
            {BTW_REGELS.map(r => (
              <label key={r.key} className="flex items-center gap-2 cursor-pointer">
                <div className={`w-7 h-3.5 rounded-full relative transition-colors shrink-0 ${btwRegel === r.key ? 'bg-[#002FA7]' : 'bg-gray-300'}`}
                  onClick={() => setBtwRegel(btwRegel === r.key ? null : r.key)} data-testid={`btw-toggle-${r.key}`}>
                  <div className={`w-3 h-3 bg-white rounded-full absolute top-[1px] transition-all shadow-sm ${btwRegel === r.key ? 'left-[15px]' : 'left-[1px]'}`} />
                </div>
                <span className="text-[11px] text-gray-600 leading-tight">{r.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2" style={{ background: '#fff', border: '1px solid #E5E5E5' }}>
          <div className="px-4 py-2 border-b border-gray-200" style={{ background: '#F0F4FF' }}>
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#002FA7]">Totalen</span>
          </div>
          <div className="p-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Subtotaal:</span><span className="font-medium">{fmt(subtotaal)}</span></div>
              {fKorting > 0 && <div className="flex justify-between text-red-600"><span>Korting:</span><span>- {fmt(fKorting)}</span></div>}
              {Object.entries(btwGroups).map(([pct, base]) => (
                parseFloat(pct) > 0 && <div key={pct} className="flex justify-between" data-testid={`document-vat-summary-${pct}`}><span className="text-gray-500">BTW {vatLabel(pct)}:</span><span>{fmt(base * parseFloat(pct) / 100)}</span></div>
              ))}
              <div className="flex justify-between pt-2 text-base font-bold" style={{ fontFamily: 'var(--font-heading)', borderTop: '2px solid #002FA7' }}>
                <span className="text-[#002FA7]">Totaal:</span><span className="text-[#002FA7]">{fmt(totaal)}</span>
              </div>
            </div>
            <button className="text-[#002FA7] text-[10px] hover:underline mt-2" onClick={() => {
              const val = prompt('Financiële korting (EUR):', financieleKorting);
              if (val !== null) setFinancieleKorting(val);
            }} data-testid="add-korting-btn"><Plus size={8} className="inline mr-0.5" />Financiële korting</button>
            {type === 'factuur' && (
              <div className="mt-3 p-2.5 bg-[#FEF3C7] border border-[#FDE68A] text-[10px] text-[#92400E]" data-testid="betalingskorting-info">
                <label className="flex items-center gap-1.5 font-bold cursor-pointer">
                  <input
                    type="checkbox"
                    checked={betKortAan}
                    onChange={(e) => {
                      setBetKortAan(e.target.checked);
                      if (e.target.checked) {
                        // Bij aanvinken: pre-fill met klant- of instellingsdefaults
                        if (!betKortDagen) setBetKortDagen(selectedKlant.betalingskorting_dagen || inst.betalingskorting_dagen || '');
                        if (!betKortPct && !betKortBedrag) setBetKortPct(selectedKlant.betalingskorting_pct || inst.betalingskorting_pct || '');
                      }
                    }}
                    data-testid="betkort-aan"
                  />
                  Betalingskorting bij vroege betaling
                </label>
                {betKortAan && (
                  <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                    <div>
                      <div className="text-[9px] uppercase opacity-70">Dagen</div>
                      <input type="number" min="0" className="field-input text-[10px] h-7" value={betKortDagen}
                             onChange={(e) => setBetKortDagen(e.target.value)} placeholder="8" data-testid="betkort-dagen" />
                    </div>
                    <div>
                      <div className="text-[9px] uppercase opacity-70">% korting</div>
                      <input type="number" min="0" step="0.1" className="field-input text-[10px] h-7" value={betKortPct}
                             onChange={(e) => { setBetKortPct(e.target.value); if (e.target.value) setBetKortBedrag(''); }}
                             placeholder="2" data-testid="betkort-pct" />
                    </div>
                    <div>
                      <div className="text-[9px] uppercase opacity-70">€ vast</div>
                      <input type="number" min="0" step="0.01" className="field-input text-[10px] h-7" value={betKortBedrag}
                             onChange={(e) => { setBetKortBedrag(e.target.value); if (e.target.value) setBetKortPct(''); }}
                             placeholder="10,00" data-testid="betkort-bedrag" />
                    </div>
                  </div>
                )}
                {betKortAan && totaal > 0 && (parseFloat(betKortPct) > 0 || parseFloat(betKortBedrag) > 0) && (() => {
                  const kortEur = parseFloat(betKortBedrag) > 0
                    ? Math.min(parseFloat(betKortBedrag), totaal)
                    : totaal * (parseFloat(betKortPct) / 100);
                  return (
                    <div className="mt-1.5 text-[10px]">
                      Te betalen bij vroege betaling: <strong>{fmt(totaal - kortEur)}</strong>
                      <span className="ml-1 opacity-70">(korting: {fmt(kortEur)})</span>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Opmerking + Bijlagen + Actions */}
      <div className="flex gap-3 items-start mb-4">
        <textarea className="field-input flex-1 text-sm" rows={2} placeholder="Opmerking / nota (optioneel)" value={nota} onChange={e => { notaEdited.current = true; setNota(e.target.value); }} data-testid="doc-nota" style={{ padding: '6px 10px' }} />
        <div className="flex gap-2 shrink-0 items-center">
          {saveMsg && (
            <span className="text-xs font-bold text-[#008A27] bg-green-50 px-2 py-1 border border-green-200 animate-pulse" data-testid="save-msg">
              {saveMsg}
            </span>
          )}
          {dirty && !saveMsg && (
            <span
              className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 px-2 py-1 border border-amber-300 animate-pulse"
              data-testid="unsaved-indicator"
              title="Je hebt wijzigingen die nog niet opgeslagen zijn. Klik op Opslaan om de wijzigingen te bewaren voordat je PDF genereert of verstuurt."
            >
              ✏️ Niet opgeslagen
            </span>
          )}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => goBack()} data-testid="cancel-doc-btn">Terug</button>
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={saving || (klantAlerts?.has_kritiek) || selectedKlant?.geblokkeerd}
            title={
              selectedKlant?.geblokkeerd
                ? 'Klant is geblokkeerd — document opslaan niet toegestaan'
                : klantAlerts?.has_kritiek
                  ? 'Klant is niet meer actief - bevestig eerst via Notificaties'
                  : undefined
            }
            data-testid="save-doc-btn"
          >
            {saving ? 'Opslaan...' : klantAlerts?.has_kritiek ? '🚫 Geblokkeerd' : isEdit ? 'Bewaren' : (isSchema ? 'Schema aanmaken' : `${typeInfo.label} aanmaken`)}
          </button>
        </div>
      </div>

      {/* Bijlagen (alleen bij bewerken) */}
      {isEdit && (
        <div className="space-y-4 mb-8">
          {/* ── Betalingen ── */}
          {(type === 'factuur' || type === 'creditnota') && (() => {
            const betaaldTotaal = betalingen.reduce((s,b) => s + Math.abs(b.bedrag||0), 0);
            const openstaand = Math.max(0, totaal - betaaldTotaal);
            const overbetaald = Math.max(0, betaaldTotaal - totaal);
            return (
            <div className="bg-white border border-gray-200" data-testid="betalingen-sectie">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-2 text-sm font-bold"><CurrencyEur size={16} className="text-[#008A27]" /> Betalingen</div>
                <button className="text-xs text-[#002FA7] font-bold hover:underline flex items-center gap-1" onClick={() => { setShowBetForm(!showBetForm); if (!showBetForm) setBetForm(p => ({ ...p, bedrag: openstaand > 0 ? String(openstaand.toFixed(2)) : String(totaal.toFixed(2)), datum: today() })); }} data-testid="add-betaling-btn">
                  <Plus size={12} /> Betaling toevoegen
                </button>
              </div>

              {/* Saldo overzicht */}
              <div className="px-4 py-2 flex gap-6 border-b border-gray-50 bg-gray-50 text-[11px]">
                <div><span className="text-gray-400">Factuurbedrag:</span> <span className="font-bold">{fmt(totaal)}</span></div>
                <div><span className="text-gray-400">Betaald:</span> <span className="font-bold text-[#008A27]">{fmt(betaaldTotaal)}</span></div>
                {openstaand > 0 && <div><span className="text-gray-400">Openstaand:</span> <span className="font-bold text-[#E02424]">{fmt(openstaand)}</span></div>}
                {overbetaald > 0.01 && <div><span className="text-gray-400">Overbetaald:</span> <span className="font-bold text-[#7C3AED]">{fmt(overbetaald)}</span></div>}
              </div>

              {showBetForm && (
                <div className="p-4 bg-gray-50 border-b border-gray-100 flex gap-3 items-end flex-wrap" data-testid="betaling-form">
                  <div><label className="form-label text-[10px]">Datum</label><input type="date" className="field-input text-xs" value={betForm.datum} onChange={e => setBetForm(p => ({ ...p, datum: e.target.value }))} /></div>
                  <div><label className="form-label text-[10px]">Bedrag</label><input type="number" step="0.01" className="field-input text-xs font-bold" value={betForm.bedrag} onChange={e => setBetForm(p => ({ ...p, bedrag: e.target.value }))} placeholder={fmt(openstaand > 0 ? openstaand : totaal)} data-testid="bet-bedrag" /></div>
                  <div><label className="form-label text-[10px]">Betaalwijze</label>
                    <select className="field-input text-xs" value={betForm.betaalwijze} onChange={e => setBetForm(p => ({ ...p, betaalwijze: e.target.value }))}>
                      <option>Bankverrichting</option><option>Cash</option><option>Domiciliëring</option><option>Cheque</option><option>Stripe</option>
                    </select></div>
                  <div className="flex-1"><label className="form-label text-[10px]">Notities</label><input className="field-input text-xs" value={betForm.notities} onChange={e => setBetForm(p => ({ ...p, notities: e.target.value }))} placeholder="Optioneel" /></div>
                  <button className="btn btn-primary btn-sm" onClick={addBetaling} data-testid="save-betaling-btn">Opslaan</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowBetForm(false)}>Annuleren</button>
                </div>
              )}
              <table className="data-table text-xs">
                <thead><tr><th>#</th><th>Datum</th><th className="right">Bedrag</th><th>Betaalwijze</th><th>Mededeling</th><th className="right">Saldo na</th><th className="right w-10"></th></tr></thead>
                <tbody>
                  {betalingen.map((b, idx) => {
                    const saldoNa = totaal - betalingen.slice(0, idx + 1).reduce((s, x) => s + Math.abs(x.bedrag||0), 0);
                    return (
                      <tr key={b.id}>
                        <td className="text-gray-400">{idx + 1}</td>
                        <td>{b.datum}</td>
                        <td className="right font-bold text-[#008A27]">{fmt(Math.abs(b.bedrag || 0))}</td>
                        <td>{b.bron || 'Bankverrichting'}</td>
                        <td className="text-gray-400 font-mono text-[10px]">{b.meded || '-'}</td>
                        <td className={`right font-bold ${saldoNa > 0.01 ? 'text-[#E02424]' : saldoNa < -0.01 ? 'text-[#7C3AED]' : 'text-[#008A27]'}`}>
                          {saldoNa < -0.01 ? `- ${fmt(Math.abs(saldoNa))}` : fmt(Math.max(0, saldoNa))}
                          {saldoNa < -0.01 && <span className="ml-1 text-[9px] bg-purple-50 text-[#7C3AED] px-1">overbetaald</span>}
                          {Math.abs(saldoNa) < 0.02 && <span className="ml-1 text-[9px] bg-green-50 text-green-700 px-1">voldaan</span>}
                        </td>
                        <td className="right">
                          <button
                            type="button"
                            onClick={() => delBetaling(b)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Betaling verwijderen"
                            data-testid={`del-betaling-${b.id}`}
                          >
                            <Trash size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {betalingen.length === 0 && <tr><td colSpan="7" className="text-center text-gray-300 py-4">Nog geen betalingen ontvangen</td></tr>}
                </tbody>
              </table>
            </div>
            );
          })()}

          {/* ── Betalingsherinneringen ── */}
          {(type === 'factuur') && (
            <div className="bg-white border border-gray-200" data-testid="herinneringen-sectie">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-2 text-sm font-bold"><Bell size={16} className="text-[#E5B300]" /> Betalingsherinneringen</div>
                <button type="button" className="text-xs text-[#B91C1C] font-bold hover:underline" onClick={() => setBhModalOpen(true)} data-testid="herinneringen-sectie-open-btn">
                  + Betalingsherinnering opmaken
                </button>
              </div>
              {bhHistoriek.length > 0 && (
                <div className="divide-y divide-gray-100" data-testid="bh-historiek-factuur">
                  {bhHistoriek.map(h => {
                    const NIV = { 1: { l: '1e Herinnering', k: '#002FA7' }, 2: { l: '2e Herinnering', k: '#EA580C' }, 3: { l: 'Ingebrekestelling', k: '#B91C1C' } };
                    const n = NIV[h.niveau] || NIV[1];
                    const KANAAL_ICON = { pdf: '📄', email: '✉️', whatsapp: '💬' };
                    const d = h.datum ? new Date(h.datum) : null;
                    return (
                      <div key={h.id} className="px-4 py-2 flex items-center gap-3 text-xs" data-testid={`bh-historiek-factuur-${h.id}`}>
                        <span>{KANAAL_ICON[h.kanaal] || '📄'}</span>
                        <span className="font-bold" style={{ color: n.k }}>{n.l}</span>
                        <span className="text-gray-400">{d ? d.toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <table className="data-table text-xs">
                <thead><tr><th>Datum</th><th>Type herinnering</th></tr></thead>
                <tbody>
                  {herinneringen.map(h => (
                    <tr key={h.id} className={h.stap >= 3 ? 'bg-blue-50' : ''}><td>{h.datum}</td><td>{h.stapNm || `Stap ${h.stap}`}</td></tr>
                  ))}
                  {herinneringen.length === 0 && bhHistoriek.length === 0 && <tr><td colSpan="2" className="text-center text-gray-300 py-4">Geen herinneringen verstuurd</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Bijlagen ── */}
          <div className="bg-white border border-gray-200" data-testid="bijlagen-sectie">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2 text-sm font-bold"><Paperclip size={16} className="text-gray-500" /> Bijlagen</div>
            </div>
            <div className="p-4">
              <BijlagenPanel docId={id} />
            </div>
          </div>

          {/* ── Leveringsfoto's (schade/bewijs) — alleen leverbon ── */}
          {isEdit && type === 'leverbon' && (
            <div className="bg-white border border-gray-200" data-testid="lever-fotos-sectie">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-2 text-sm font-bold"><Camera size={16} className="text-gray-500" /> Leveringsfoto's</div>
                <span className="text-[10px] text-gray-400">Verschijnen als bijlage-pagina op de leverbon-PDF</span>
              </div>
              <div className="p-4">
                <LeverFotosPanel
                  listFotos={() => API.get(`/documenten/${id}/lever-fotos`).then(r => r.data)}
                  uploadFoto={(file, opm) => { const fd = new FormData(); fd.append('file', file); fd.append('opmerking', opm || ''); return API.post(`/documenten/${id}/lever-foto`, fd); }}
                  deleteFoto={(fid) => API.delete(`/documenten/${id}/lever-foto/${fid}`)}
                  updateOpmerking={(fid, opm) => API.put(`/documenten/${id}/lever-foto/${fid}`, { opmerking: opm })}
                />
              </div>
            </div>
          )}

          {/* ── Mailverkeer ── */}
          {isEdit && <DocMailsPanel docId={id} />}

          {/* ── Historiek ── */}
          <div className="bg-white border border-gray-200" data-testid="historiek-sectie">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2 text-sm font-bold"><ClockCounterClockwise size={16} className="text-gray-400" /> Historiek</div>
            </div>
            <table className="data-table text-xs">
              <thead><tr><th>Datum</th><th>Actie</th></tr></thead>
              <tbody>
                {historiek.map((h, i) => (
                  <tr key={i}><td className="text-gray-400 whitespace-nowrap">{h.tijdstip ? new Date(h.tijdstip).toLocaleString('nl-BE') : '-'}</td><td>{h.actie} {h.entiteit} {h.details ? `— ${h.details}` : ''}</td></tr>
                ))}
                {historiek.length === 0 && <tr><td colSpan="2" className="text-center text-gray-300 py-4">Geen historiek beschikbaar</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetect={handleScanned}
        title="Scan artikel-barcode"
        continuous={true}
      />

      {/* Variant-picker voor document-regels — verschijnt wanneer artikel varianten heeft */}
      {variantPicker && (
        <VariantPickerModal
          artikel={variantPicker.artikel}
          open={true}
          onClose={() => setVariantPicker(null)}
          onSelect={applyVariantToLine}
          prijsGroep={prijsGroep}
          preselectVariant={variantPicker.preselectVariant || null}
          allowZeroStock={true}
          actieLabel="Op document"
          onSwitchToMatrix={() => {
            const { artikel, lineIdx } = variantPicker;
            setVariantPicker(null);
            setVariantMatrix({ artikel, lineIdx });
          }}
        />
      )}

      {/* Variant-matrix voor document-regels — grid om meerdere combinaties in 1 keer in te geven */}
      {variantMatrix && (() => {
        const tgt = lijnen[variantMatrix.lineIdx];
        const isFilled = !!(tgt && (tgt.art_id || tgt.omschr || pf(tgt.aantal) > 0 || pf(tgt.prijs) > 0));
        return (
          <VariantMatrixModal
            artikel={variantMatrix.artikel}
            open={true}
            onClose={() => setVariantMatrix(null)}
            onSubmit={applyMatrixToLines}
            prijsGroep={prijsGroep}
            targetLineIsFilled={isFilled}
          />
        );
      })()}

      {versturenOpen && isEdit && (
        <VersturenModal
          doc={{ id, type }}
          klant={klanten.find(x => x.id === klantId)}
          onClose={() => setVersturenOpen(false)}
        />
      )}

      {mailPreviewOpen && isEdit && (
        <MailPreviewModal
          docId={id}
          lang="auto"
          onClose={() => setMailPreviewOpen(false)}
          onSent={() => setMailPreviewOpen(false)}
        />
      )}

      {verzendOpen && isEdit && type === 'leverbon' && (() => {
        const k = klanten.find(x => x.id === klantId) || {};
        // Probeer straat + huisnummer te splitsen uit "adres" of leveringsadres
        const fullAdres = (k.lever_adres || k.adres || '').trim();
        const m = fullAdres.match(/^(.*?)\s+(\d+\w*)$/);
        const street = m ? m[1] : fullAdres;
        const house_number = m ? m[2] : '';
        return (
          <VerzendlabelModal
            open={true}
            onClose={() => setVerzendOpen(false)}
            bronType="leverbon"
            bronId={id}
            initial={{
              name: k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`.trim() || k.naam || '',
              company: k.bedrijf || '',
              street,
              house_number,
              postal_code: k.lever_postcode || k.postcode || '',
              city: k.lever_gemeente || k.gemeente || '',
              country: (k.lever_land || k.land || 'BE').toUpperCase(),
              email: k.email || '',
              telephone: k.tel || k.gsm || '',
            }}
          />
        );
      })()}

      {/* Packing List preview modal (bewerkbaar: pallets/gewichten) */}
      {packing && (
        <PackingListModal docId={id} initial={packing} onClose={() => setPacking(null)} />
      )}

      {/* Betalingsherinnering-modal — geopend vanuit factuur-actie */}
      {type === 'factuur' && (
        <BetalingsherinneringModal
          open={bhModalOpen}
          onClose={() => setBhModalOpen(false)}
          klantId={klantId}
          klantNaam={(klanten.find(k => k.id === klantId) || {}).bedrijf || (klanten.find(k => k.id === klantId) || {}).naam || ''}
          klantEmail={(klanten.find(k => k.id === klantId) || {}).email || ''}
          klantGsm={(klanten.find(k => k.id === klantId) || {}).gsm || (klanten.find(k => k.id === klantId) || {}).tel || ''}
          klantTaal={(klanten.find(k => k.id === klantId) || {}).taal || 'nl'}
          preselectIds={id ? [id] : []}
        />
      )}
    </div>
  );
}

// ── Helper: handtekening-blok voor PDF/print ──────────────────
function buildHandtekeningBlok(d, ist) {
  if (d.type !== 'leverbon' || !d.handtekening) return '';
  const tekst = (ist?.leverbon_handtekening_tekst || 'Voor ontvangst in goede staat getekend door:').replace(/</g, '&lt;');
  const naam = (d.handtekening_naam || '').replace(/</g, '&lt;');
  let datumStr = '';
  try { datumStr = new Date(d.handtekening_datum).toLocaleString('nl-BE'); } catch { datumStr = d.handtekening_datum || ''; }
  return `
<div style="margin-top:30px;padding:16px;border:2px dashed #059669;border-radius:6px;background:#F0FDF4;page-break-inside:avoid">
  <div style="font-size:10px;text-transform:uppercase;font-weight:700;color:#059669;letter-spacing:0.08em;margin-bottom:8px">✓ Digitaal ondertekend</div>
  <div style="font-size:11px;color:#333;margin-bottom:8px">${tekst}</div>
  <div style="display:flex;gap:16px;align-items:flex-end">
    <div style="flex:1">
      <div style="background:#fff;border:1px solid #E5E5E5;border-radius:4px;padding:8px;min-height:80px;display:flex;align-items:center;justify-content:center">
        <img src="${d.handtekening}" alt="Handtekening" style="max-height:80px;max-width:100%;object-fit:contain" />
      </div>
    </div>
    <div style="flex:1;font-size:10px;color:#555">
      <div><strong>Naam:</strong> ${naam || '—'}</div>
      <div><strong>Datum &amp; tijd:</strong> ${datumStr}</div>
      ${d.handtekening_ip ? `<div><strong>IP:</strong> <code style="font-size:9px;color:#888">${d.handtekening_ip}</code></div>` : ''}
      <div style="margin-top:6px;font-size:9px;color:#888;font-style:italic">Geldig als bewijs van ontvangst</div>
    </div>
  </div>
</div>`;
}

// ── Helper: knoppen om document om te zetten naar een ander type ─
// Creditnota kan enkel vanuit factuur — daarom uitgesloten bij leverbon/proforma
const CONVERSIES = {
  offerte:    ['factuur', 'proforma', 'bestelbon', 'leverbon'],
  proforma:   ['factuur', 'leverbon', 'bestelbon'],
  bestelbon:  ['leverbon', 'factuur'],
  leverbon:   ['factuur'],
  factuur:    ['leverbon', 'bestelbon', 'creditnota', 'proforma'],
  creditnota: [],
};
const CONV_LABELS = { factuur: 'Factuur', leverbon: 'Leverbon', bestelbon: 'Bestelbon', proforma: 'Proforma', creditnota: 'Creditnota', offerte: 'Offerte' };
const CONV_KLEUREN = { factuur: '#002FA7', leverbon: '#059669', bestelbon: '#0E7490', proforma: '#B45309', creditnota: '#DC2626', offerte: '#7C3AED' };

function ConversieKnoppen({ huidigType, docId, docStatus, onConverted }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  // Afgewerkte bestelbon (volledig geleverd): geen leverbon/factuur meer aanmaken
  const afgewerkt = huidigType === 'bestelbon' && (docStatus || '').toLowerCase() === 'afgewerkt';
  const opties = afgewerkt ? [] : (CONVERSIES[huidigType] || []);
  if (!opties.length) return null;

  const omzetten = async (naar, force = false) => {
    if (busy) return;
    if (!force && !window.confirm(`${CONV_LABELS[naar]} aanmaken op basis van dit document?`)) return;
    setBusy(true);
    try {
      const endpoint = naar === 'creditnota' && huidigType !== 'creditnota' ? `/documenten/${docId}/creditnota` : `/documenten/${docId}/omzetten`;
      const body = naar === 'creditnota' && huidigType !== 'creditnota' ? {} : { naar_type: naar, ...(force ? { force: true } : {}) };
      const { data } = await API.post(endpoint, body);
      // Leverbon → factuur: aanmaken + opslaan, maar op de leverbon blijven (user opent de factuur zelf indien nodig)
      if (huidigType === 'leverbon' && naar === 'factuur' && data?.id) {
        toast.success(`Factuur ${data.id} aangemaakt en opgeslagen`, {
          duration: 8000,
          action: { label: 'Openen', onClick: () => nav(`/documenten/${data.id}/bewerk`) },
        });
        onConverted?.(data);
      } else {
        nav(`/documenten/${data.id}/bewerk`);
      }
    } catch (e) {
      // Offerte voor niet-klant: eerst klantfiche aanmaken (automatisch voorgesteld), dan opnieuw omzetten
      const detail0 = e.response?.data?.detail || e.message;
      if (e.response?.status === 409 && /PROSPECT_ZONDER_KLANT/.test(detail0)) {
        const firma = (detail0.match(/niet-klant '([^']*)'/) || [])[1] || 'deze prospect';
        if (window.confirm(`Deze offerte is voor niet-klant "${firma}".\n\nKlantfiche aanmaken uit de offertegegevens en meteen ${CONV_LABELS[naar]?.toLowerCase() || 'het document'} aanmaken?`)) {
          try {
            await API.post(`/documenten/${docId}/prospect-naar-klant`, {});
            setBusy(false);
            return omzetten(naar, true);
          } catch (e2) { alert('Klant aanmaken mislukt: ' + (e2.response?.data?.detail || e2.message)); }
        }
        setBusy(false);
        return;
      }
      // Bij stroom-conflict (409): bied keuze "Bewerk bestaande" / "Toch nieuwe aanmaken"
      const detail = e.response?.data?.detail || e.message;
      if (e.response?.status === 409 && /Er bestaat al een|reeds.*omgezet|al omgezet naar/i.test(detail)) {
        const idMatch = detail.match(/([A-Z]+-\d{4}-\d+)/);
        const bestaandId = idMatch ? idMatch[1] : null;
        if (bestaandId) {
          const keuze = window.confirm(
            `${detail}\n\n` +
            `[OK] = Bewerk bestaand document (${bestaandId})\n` +
            `[Annuleer] = Toch nieuwe ${CONV_LABELS[naar]} aanmaken`
          );
          if (keuze) {
            nav(`/documenten/${bestaandId}/bewerk`);
          } else {
            // user wil toch nieuwe → re-roep met force=true
            setBusy(false);
            return omzetten(naar, true);
          }
        } else {
          alert(detail);
        }
      } else {
        alert(detail);
      }
    }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-gray-200" data-testid="conversie-panel">
      <div className="px-3 py-2 border-b border-gray-100">
        <span className="text-[10px] font-bold text-gray-400">Omzetten naar…</span>
      </div>
      <div className="p-2 grid grid-cols-2 gap-1.5">
        {opties.map(naar => (
          <button
            key={naar}
            onClick={() => omzetten(naar)}
            disabled={busy}
            className="px-2 py-2 rounded text-[11px] font-bold border transition active:scale-95 flex items-center justify-center gap-1"
            style={{ borderColor: CONV_KLEUREN[naar], color: CONV_KLEUREN[naar], background: '#fff' }}
            data-testid={`conv-btn-${naar}`}
          >
            {busy ? '…' : <>→ {CONV_LABELS[naar]}</>}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Helper: Leverbon handtekening panel ─────────────────────────
function HandtekeningPanel({ docId, klantNaam, inst, onVerzend }) {
  const [doc, setDoc] = useState(null);
  const [toonPad, setToonPad] = useState(false);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  const handtekeningAan = inst?.leverbon_handtekening_actief !== false;

  const load = async () => {
    try {
      const r = await API.get(`/documenten/${docId}`);
      setDoc(r.data || null);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [docId]);

  if (!handtekeningAan) return (
    <div className="bg-white border border-gray-200">
      <div className="px-3 py-2 border-b border-gray-100"><span className="text-[10px] font-bold text-gray-400">Handtekening</span></div>
      <div className="p-3 text-[10px] text-gray-400 text-center">Uitgeschakeld in Instellingen → Documenten → Leverbon</div>
    </div>
  );

  const opslaan = async ({ dataUrl, naam }) => {
    setBusy(true);
    try {
      await API.post(`/documenten/${docId}/handtekening`, { handtekening: dataUrl, naam });
      setToonPad(false);
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const verwijder = async () => {
    if (!window.confirm('Handtekening verwijderen?')) return;
    await API.delete(`/documenten/${docId}/handtekening`);
    load();
  };

  const maakLink = async () => {
    try {
      const { data } = await API.post(`/documenten/${docId}/teken-link`);
      const url = `${window.location.origin}/lever-tekenen/${data.token}`;
      setLink({ url, expires: data.expires });
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const handtekening = doc?.handtekening;
  const htDatum = doc?.handtekening_datum;

  return (
    <div className="bg-white border border-gray-200" data-testid="handtekening-panel">
      <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
        <span className="text-[10px] font-bold text-gray-400">Handtekening ontvanger</span>
        {handtekening && <span className="text-[9px] text-green-600 font-bold">✓ ONDERTEKEND</span>}
      </div>
      <div className="p-3 space-y-2">
        {handtekening ? (
          <>
            <div className="p-2 bg-gray-50 rounded border border-gray-200">
              <img src={handtekening} alt="Handtekening" className="w-full max-h-24 object-contain bg-white rounded" data-testid="ht-preview" />
              <div className="text-[10px] text-gray-600 mt-1">
                <div><b>{doc?.handtekening_naam || '-'}</b></div>
                <div>{new Date(htDatum).toLocaleString('nl-BE')}</div>
              </div>
            </div>
            <button onClick={verwijder} className="btn btn-ghost btn-sm w-full justify-center text-red-600" data-testid="ht-wis">Verwijderen</button>
          </>
        ) : toonPad ? (
          <SignaturePad
            width={600}
            height={180}
            initialName={klantNaam}
            onSave={opslaan}
            onCancel={() => setToonPad(false)}
          />
        ) : (
          <>
            <div className="flex gap-2">
              <button className="btn btn-primary justify-center flex-1" onClick={() => setToonPad(true)} disabled={busy} data-testid="ht-start">
                Laten tekenen (tablet)
              </button>
              {onVerzend && (
                <button
                  type="button"
                  onClick={onVerzend}
                  className="btn btn-primary justify-center flex-1"
                  style={{ background: '#FF6B0D', borderColor: '#FF6B0D' }}
                  data-testid="leverbon-sendcloud-knop"
                  title="Maak een verzendlabel via Sendcloud"
                >
                  📦 Verzendlabel
                </button>
              )}
            </div>
            <button className="btn btn-ghost w-full justify-center text-xs" onClick={maakLink} data-testid="ht-link">
              Teken-link voor klant (via smartphone)
            </button>
            {link && (
              <div className="p-2 rounded bg-blue-50 border border-blue-200 text-[10px]">
                <div className="font-bold text-blue-700 mb-1">Link voor klant (vervalt na ondertekening)</div>
                <code className="block break-all text-[9px] bg-white p-1 rounded">{link.url}</code>
                <button className="mt-1 text-[9px] text-blue-700 underline" onClick={() => navigator.clipboard?.writeText(link.url)}>Kopieer</button>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
}
